/* eslint-disable @typescript-eslint/no-use-before-define */
import React, {
  type HTMLAttributes,
  type LabelHTMLAttributes,
  type SelectHTMLAttributes,
  type ChangeEventHandler,
  type InputHTMLAttributes,
  type DragEventHandler,
  type TextareaHTMLAttributes,
  type ReactNode,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type SyntheticEvent,
  forwardRef,
  useCallback,
  useState,
  useEffect,
  CSSProperties,
  FocusEventHandler,
} from 'react';
import type { UUID } from 'crypto';
import { Link, type LinkProps } from 'react-router-dom';
import { FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';

export const ContentsHandlerContext = React.createContext<Types.ContentsHandlerContextValue>({
  t: (key: string) => key,
  onChangeArtistSameAsAccount: () => {},
  onClickTemplateAddressSearch: () => {},
  applyType: 'GROUP',
  templateMembersIds: [crypto.randomUUID()],
  setTemplateMembersIds: () => {},
  onChangeApplyType: () => {},
  onChangeTemplateMembersNum: () => {},
  onClickTemplateMembersDelete: () => {},
  isMoreSelected: [],
  onChangeCheckParts: () => {},
  onChangeSelectDateParts: () => {},
  hasErrorInSelectDate: false,
  hasErrorInMemberConsent: false,
  hasErrorConsentFiles: false,
  onChangePreferredVenueSelect: () => {},
});

type ToApplyFormListOpts = {
  applyFormNoOffset?: number;
};
interface ToItemListOptions {
  ['template:address']?: {
    prefectures?: { label: string; value: string }[];
  };
  ['template:artist']?: {};
  ['template:venue']?: {
    preferred_venue_list?: { label: string; value: string }[];
  };
  image?: {
    getSrc: (src: string) => string;
  };
  isGlobal?: boolean;
}
export class ContentsHandler {
  /** 表示言語 */
  public static langCode: `${number}` = '0';

  public static toElement(config: Types.PreviewItem | Types.Item): React.ReactNode {
    if (config.element === 'gohst') {
      return ContentsHandler.toGohst(config);
    } else if (config.element === 'text') {
      return ContentsHandler.toText(config);
    } else if (config.element === 'input') {
      if (config.type === 'file') {
        return ContentsHandler.toFileInput(config);
      } else {
        return ContentsHandler.toInput(config);
      }
    } else if (config.element === 'image') {
      return ContentsHandler.toImage(config);
    } else if (config.element === 'hr') {
      return ContentsHandler.toHr(config);
    } else if (config.element === 'check') {
      return ContentsHandler.toCheck(config);
    } else if (config.element === 'select-date') {
      return ContentsHandler.toSelectDate(config);
    } else if (config.element === 'select') {
      return ContentsHandler.toSelect(config);
    } else if (config.element === 'template:artist') {
      return ContentsHandler.toTemplateArtist(config);
    } else if (config.element === 'template:venue') {
      return ContentsHandler.toTemplateVenue(config);
    } else if (config.element === 'template:address') {
      return ContentsHandler.toTemplateAddress(config);
    } else if (config.element === 'template:consent') {
      return ContentsHandler.toTemplateConsent(config);
    } else if (config.element === 'template:members') {
      return ContentsHandler.toTemplateMembers(config);
    } else if (config.element === 'parallel-input') {
      return ContentsHandler.toParallelInput(config);
    } else if (config.element === 'dropdown-list') {
      return ContentsHandler.toDropdownList(config);
    } else if (config.element === 'category') {
      return ContentsHandler.toCategory(config);
    }
    return null;
  }

  public static toGohst(config: Types.PreviewGohst): React.ReactNode {
    if (config.type === 'image') {
      return (
        <div className="text-center py-16 border-dashed border-2 rounded-sm">ここに画像を追加</div>
      );
    } else {
      return (
        <div className="text-center py-2 border-dashed border-2 rounded-sm">ここに要素を追加</div>
      );
    }
  }

  public static toText(
    config: Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const { alignment, formats, color = Consts.DEFAULT_VALUES.TEXT_COLOR } = config;
    const text = config[`text.${langCode}`];
    if (text) {
      const classNames = ['px-2 whitespace-pre-line', alignment ? `text-${alignment}` : null];
      if (formats) {
        if (formats.includes('underlined')) {
          classNames.push('underline');
        }
        if (formats.includes('bold')) {
          classNames.push('font-bold');
        }
        if (formats.includes('italic')) {
          classNames.push('italic');
        }
      }
      let fontSize: string = '1em';
      if (config.size) {
        const sizeNum = parseFloat(config.size);
        if (!isNaN(sizeNum)) {
          fontSize = `${sizeNum}em`;
        }
      }
      return (
        <p className={classNames.join(' ')} style={{ color, fontSize }}>
          {text}
        </p>
      );
    } else {
      return (
        <div className="text-center py-2 border-dashed border-2 rounded-sm text-black">
          ここにテキストを追加
        </div>
      );
    }
  }

  public static toFileInput(config: Types.PreviewInputFile | Types.InputFile): React.ReactNode {
    const { langCode } = ContentsHandler;
    const labelConfig = ContentsHandler.extractLabelConfig(config);
    const { preview, required, fileType } = config;
    const accept =
      fileType === 'image'
        ? '.jpeg,.jpg,.png,.heic'
        : fileType === 'video'
          ? '.mp4,.mov,.3gp,.m4v,.wmv'
          : '.aac,.aiff,.amr,.flac,.m4a,.mp3,.ogg,.wav,.wma,.alac';
    const ariaId = config.uuid;
    // label
    const labelProps: Components.FormLabelProps = {
      htmlFor: ariaId,
      required,
    };
    // input
    const props: React.InputHTMLAttributes<HTMLInputElement> = {
      ...(preview ? { readOnly: true } : {}),
      required,
      accept,
    };
    // ios用
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
      const element = event.currentTarget;
      if (element.type !== 'file') {
        return;
      }
      try {
        const rect = element.getBoundingClientRect();
        if (1 < rect.top && rect.bottom < window.innerHeight) return;

        element.scrollIntoView({
          block: 'center',
          behavior: 'auto',
        });
      } catch (e) {
        console.error(e);
      }
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ register }) => (
          <Components.FormUnit>
            <Components.FormLabel {...labelProps}>
              {labelConfig[`text.${langCode}`] && ContentsHandler.toText(labelConfig)}
            </Components.FormLabel>
            <Components.FileInput
              {...(isIOS ? { onFocus: onFocus } : {})}
              {...(!preview && register ? register(`${config.apply_form_no}` as const) : {})}
              label={config[`placeholder.${langCode}`]}
              {...props}
            />
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toInput(
    config: Types.PreviewInput | Types.Input | Types.PreviewInputLink | Types.InputLink
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const labelConfig = ContentsHandler.extractLabelConfig(config);

    const maxLength = (() => {
      const maxLength = parseInt(config.maxLength ?? '');
      return isNaN(maxLength) ? 200 : maxLength;
    })();
    const { preview, required } = config;
    const pattern = (() => {
      if (
        config.type !== 'text' ||
        config.validation === undefined ||
        config.validation === Consts.VALIDATION.NONE
      ) {
        return Consts.NONE_REGEX.source;
      }
      if (config.validation === Consts.VALIDATION.NUMERIC) {
        return Consts.NUMERIC_REGEX.source;
      }
      if (config.validation === Consts.VALIDATION.HALF) {
        return Consts.HALF_REGEX.source;
      }
      return undefined;
    })();
    const title = '絵文字等は利用できません。';
    const ariaId = config.uuid;
    // label
    const labelProps: Components.FormLabelProps = {
      htmlFor: ariaId,
      required,
    };
    if (config.type === 'text' && config.lineBreaks === '1') {
      // textarea
      const props: React.TextareaHTMLAttributes<HTMLTextAreaElement> = {
        placeholder: config[`placeholder.${langCode}`],
        maxLength,
        ...(preview ? { readOnly: true } : {}),
        required,
        id: ariaId,
      };
      return (
        <ContentsHandlerContext.Consumer>
          {({ register, errors }) => {
            const registration =
              !preview && register ? register(`${config.apply_form_no}` as const) : null;
            const errorMessage = errors?.[`${config.apply_form_no}`]?.message as string | undefined;
            return (
              <Components.FormUnit>
                <Components.FormLabel {...labelProps}>
                  {labelConfig[`text.${langCode}`] && ContentsHandler.toText(labelConfig)}
                </Components.FormLabel>
                <Components.Textarea {...props} {...registration} />
                {errorMessage && (
                  <p className="text-[color:--c-error] mt-[0.5em]">{errorMessage}</p>
                )}
              </Components.FormUnit>
            );
          }}
        </ContentsHandlerContext.Consumer>
      );
    } else {
      // input
      const props: React.InputHTMLAttributes<HTMLInputElement> = {
        type: config.type ?? 'text',
        placeholder: config[`placeholder.${langCode}`],
        maxLength,
        ...(preview ? { readOnly: true } : {}),
        required,
        pattern,
        title,
        id: ariaId,
      };
      return (
        <ContentsHandlerContext.Consumer>
          {({ register }) => (
            <Components.FormUnit>
              <Components.FormLabel {...labelProps}>
                {labelConfig[`text.${langCode}`] && ContentsHandler.toText(labelConfig)}
              </Components.FormLabel>
              <Components.Input
                {...(!preview && register ? register(`${config['apply_form_no']}` as const) : {})}
                {...props}
              />
            </Components.FormUnit>
          )}
        </ContentsHandlerContext.Consumer>
      );
    }
  }

  public static toImage(config: Types.PreviewImage | Types.Image): React.ReactNode {
    const {
      src = '',
      width = Consts.DEFAULT_VALUES.IMAGE_WIDTH,
      height = Consts.DEFAULT_VALUES.IMAGE_HEIGHT,
      alt = '',
    } = config;
    return (
      <div className="flex flex-row justify-center items-center">
        <img
          alt={alt}
          style={{ width: `${width}px`, height: `auto`, aspectRatio: `${width} / ${height}` }}
          src={src}
        />
      </div>
    );
  }

  public static toHr(config: Types.PreviewHorizontalRule | Types.HorizontalRule): React.ReactNode {
    const { width, color: borderColor = 'rgb(194, 194, 194)' } = config;
    const borderTopWidth = `${width || 1}px`;
    return <hr style={{ borderTopWidth, borderColor }} />;
  }

  public static toCheck(config: Types.PreviewCheck | Types.Check): React.ReactNode {
    const { langCode } = ContentsHandler;
    const {
      uuid,
      preview,
      required,
      selectable,
      etcInput,
      labelAndValues = [],
      direction = 'row',
    } = config;
    const labelConfig = ContentsHandler.extractLabelConfig(config);

    const ariaId = config.uuid;
    // label
    const labelProps: Components.FormLabelProps = {
      id: ariaId,
      required,
      className: 'pt-2',
    };
    const isJa = langCode === '0';
    const Component =
      parseInt(selectable ?? '-1') === 1 ? Components.Radio : Components.SelectableButton;
    const itemsContainerClassName = `flex ${direction === 'row' ? 'flex-row flex-wrap' : 'flex-col'} gap-[10px]`;
    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
      type: 'text',
      placeholder: isJa ? 'その他' : 'Others',
      maxLength: 50,
      ...(preview ? { readOnly: true } : {}),
      id: `${ariaId}.etc`,
    };

    // ios用
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
      const element = event.currentTarget;
      if (element.type !== 'radio' && element.type !== 'checkbox') {
        return;
      }

      try {
        const rect = element.getBoundingClientRect();
        if (1 < rect.top && rect.bottom < window.innerHeight) return;

        element.scrollIntoView({
          block: 'center',
          behavior: 'auto',
        });
      } catch (e) {
        console.error(e);
      }
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ register, isMoreSelected, onChangeCheckParts }) => (
          <Components.FormUnit>
            <Components.FormLabel {...labelProps}>
              {labelConfig[`text.${langCode}`] && ContentsHandler.toText(labelConfig)}
            </Components.FormLabel>
            <div className={itemsContainerClassName}>
              {labelAndValues.map(({ [`label.${langCode}` as const]: label, value = label }, i) => (
                <Component
                  key={`preview:${uuid}:check${i}`}
                  value={value}
                  {...(isIOS ? { onFocus: onFocus } : {})}
                  {...(!preview && register
                    ? register(`${config.apply_form_no}.labelAndValues` as const, {
                        onChange: (e) =>
                          onChangeCheckParts(
                            e,
                            parseInt(selectable ?? '-1'),
                            !preview ? `${config.apply_form_no}.labelAndValues` : '',
                            required
                          ),
                      })
                    : {})}
                  required={required}
                >
                  {label}
                </Component>
              ))}
            </div>
            {etcInput && (
              <Components.Input
                className="mt-[10px]"
                {...(!preview && register
                  ? register(`${config['apply_form_no']}.etc` as const)
                  : {})}
                {...inputProps}
              />
            )}
            {!preview && isMoreSelected?.includes(`${config.apply_form_no}.labelAndValues`) && (
              <p className="text-[--c-error] mt-[0.5em]">
                {isJa
                  ? `最大選択数は${selectable}個です。`
                  : `The maximum number of selections is ${selectable}.`}
              </p>
            )}
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toSelectDate(config: Types.PreviewSelectDate | Types.SelectDate): React.ReactNode {
    const { langCode } = ContentsHandler;
    const {
      uuid,
      preview,
      exclude = 0,
      required,
      'year-range': yearRange,
      'label.color': labelColor,
    } = config;
    const labelText = config[`label.text.${langCode}`];

    const includeYear = (exclude & 0b100) === 0;
    const includeMonth = (exclude & 0b010) === 0;
    const includeDate = (exclude & 0b001) === 0;

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const date = now.getDate();
    const { from = 1970, to = year + 5 } = yearRange ?? {};
    const ariaId = config.uuid;
    const isJa = langCode === '0';
    // label
    const labelProps: Components.FormLabelProps = {
      id: ariaId,
      required,
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ register, t, onChangeSelectDateParts, hasErrorInSelectDate }) => (
          <Components.FormUnit>
            <Components.FormLabel {...labelProps}>
              {labelText &&
                ContentsHandler.toText({
                  [`text.${langCode}`]: labelText,
                  color: labelColor,
                  preview,
                } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
            </Components.FormLabel>
            {/* TODO:name複数ある場合どうする？ */}
            <div className="flex flex-row items-center gap-x-2">
              {includeYear && (
                <>
                  <Components.Select
                    className={`w-fit ${required ? 'hasRequired' : ''}`}
                    defaultValue={''}
                    data-selectdate={`${config.apply_form_no}.year`}
                    {...(!preview && register
                      ? register(`${config.apply_form_no}.0` as const)
                      : {})}
                    onChange={(e) =>
                      onChangeSelectDateParts(e, `${config.apply_form_no}`, required)
                    }
                    required={required}
                    aria-labelledby={ariaId}
                  >
                    <option key={`preview:${uuid}:select-year:-`} value="">{`-`}</option>
                    {to >= from
                      ? Array(to - from + 1)
                          .fill(null)
                          .map((_, i) => (
                            <option key={`preview:${uuid}:select-year:${to - i}`} value={to - i}>
                              {to - i}
                            </option>
                          ))
                      : []}
                  </Components.Select>
                </>
              )}

              {includeMonth && (
                <Components.Select
                  className={`w-fit ${required ? 'hasRequired' : ''}`}
                  defaultValue={''}
                  data-selectdate={`${config.apply_form_no}.month`}
                  {...(!preview && register ? register(`${config.apply_form_no}.1` as const) : {})}
                  onChange={(e) => onChangeSelectDateParts(e, `${config.apply_form_no}`, required)}
                  required={required}
                  aria-labelledby={ariaId}
                >
                  <option key={`preview:${uuid}:select-month:-`} value="">{`-`}</option>
                  {Array(12)
                    .fill(null)
                    .map((_, i) => (
                      <option key={`preview:${uuid}:select-month:${i + 1}`} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                </Components.Select>
              )}

              {includeDate && (
                <Components.Select
                  className={`w-fit ${required ? 'hasRequired' : ''}`}
                  defaultValue={''}
                  data-selectdate={`${config.apply_form_no}.date`}
                  {...(!preview && register ? register(`${config.apply_form_no}.2` as const) : {})}
                  onChange={(e) => onChangeSelectDateParts(e, `${config.apply_form_no}`, required)}
                  required={required}
                  aria-labelledby={ariaId}
                >
                  <option key={`preview:${uuid}:select-date:-`} value="">{`-`}</option>
                  {Array(31)
                    .fill(null)
                    .map((_, i) => (
                      <option key={`preview:${uuid}:select-date:${i + 1}`} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                </Components.Select>
              )}
            </div>
            {!preview && hasErrorInSelectDate && (
              <p className="text-[--c-error] mt-[0.5em]">
                {isJa ? `存在しない日付です` : `This date does not exist.`}
              </p>
            )}
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toSelect(config: Types.PreviewSelect | Types.Select): React.ReactNode {
    const { langCode } = ContentsHandler;
    const { uuid, preview, [`label.text.${langCode}` as const]: label, required } = config;
    const ariaId = uuid;
    // label
    const labelProps: Components.FormLabelProps = {
      htmlFor: ariaId,
      required,
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ register }) => (
          <Components.FormUnit>
            <Components.FormLabel {...labelProps}>{label}</Components.FormLabel>
            <Components.Select
              id={ariaId}
              {...(!preview && register ? register(`${config.apply_form_no}` as const) : {})}
              required={required}
            />
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toTemplateArtist(
    config: Types.PreviewTemplateArtist | Types.TemplateArtist
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const {
      uuid,
      preview,
      showAutoFill,
      'name.label.color': nameLabelColor,
      'kana.label.color': kanaLabelColor,
      'name.required': nameRequired,
      'kana.required': kanaRequired,
    } = config;

    const nameLabelConfig = {
      [`text.${langCode}`]: `${config[`name.label.text.${langCode}`] ?? ''}`,
      color: nameLabelColor,
      preview,
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;
    const kanaLabelConfig = {
      [`text.${langCode}`]: `${config[`kana.label.text.${langCode}`] ?? ''}`,
      color: kanaLabelColor,
      preview,
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;
    const ariaIdName = `${config.uuid}:name`;
    const ariaIdKana = `${config.uuid}:kana`;
    // label
    const labelNameProps: Components.FormLabelProps = {
      htmlFor: ariaIdName,
      required: nameRequired,
    };
    const labelKanaProps: Components.FormLabelProps = {
      htmlFor: ariaIdKana,
      required: kanaRequired,
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ t, onChangeArtistSameAsAccount, register }) => (
          <React.Fragment key={`template:artist:${uuid}`}>
            <Components.FormUnit>
              <div className="flex flex-row gap-x-[10px]">
                <Components.FormLabel {...labelNameProps}>
                  {nameLabelConfig[`text.${langCode}`] && ContentsHandler.toText(nameLabelConfig)}
                </Components.FormLabel>
                {showAutoFill && (
                  <Components.Checkbox
                    className="!opacity-100"
                    onChange={(e) =>
                      onChangeArtistSameAsAccount?.(e, {
                        name: !preview ? (config['name.apply_form_no'] ?? NaN) : NaN,
                        kana: !preview ? (config['kana.apply_form_no'] ?? NaN) : NaN,
                      })
                    }
                  >
                    {t('CH_001')}
                  </Components.Checkbox>
                )}
              </div>
              <Components.Input
                type="text"
                placeholder={config[`name.placeholder.${langCode}`]}
                required={nameRequired}
                {...(preview ? { readOnly: true } : {})}
                {...(!preview && register
                  ? register(`${config['name.apply_form_no']}` as const)
                  : {})}
                id={ariaIdName}
                maxLength={100}
              />
            </Components.FormUnit>
            {
              // 言語が日本語以外の場合format=51がない場合があるため
              (preview ? true : config['kana.apply_form_no'] != null) && (
                <Components.FormUnit className="mt-[10px]">
                  <Components.FormLabel {...labelKanaProps}>
                    {kanaLabelConfig[`text.${langCode}`] && ContentsHandler.toText(kanaLabelConfig)}
                  </Components.FormLabel>
                  <Components.Input
                    type="text"
                    placeholder={config[`kana.placeholder.${langCode}`]}
                    required={kanaRequired}
                    pattern={Consts.KANA_REGEX.source}
                    {...(preview ? { readOnly: true } : {})}
                    {...(!preview && register
                      ? register(`${config['kana.apply_form_no']}` as const)
                      : {})}
                    id={ariaIdKana}
                    maxLength={100}
                  />
                </Components.FormUnit>
              )
            }
          </React.Fragment>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toTemplateVenue(
    config: Types.PreviewTemplateVenue | Types.TemplateVenue
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const {
      uuid,
      preview,
      required,
      'label.color': labelColor,
      displayNameType: displaynameType,
      'possibility.exclude': possibilityExclude,
      'possibility.color': possibilityColor,
      'note.exclude': noteExclude,
      'note.color': noteColor,
      'note.size': noteSize,
    } = config;

    const labelText = config[`label.text.${langCode}`];
    const possibilityText = config[`possibility.text.${langCode}`];
    const noteText = config[`note.text.${langCode}`];
    const labelConfig = {
      [`text.${langCode}`]: labelText,
      color: labelColor,
      preview,
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;
    const possibilityConfig = {
      [`text.${langCode}`]: possibilityText,
      color: possibilityColor,
      preview,
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;
    const noteConfig = {
      [`text.${langCode}`]: noteText,
      color: noteColor,
      size: `${noteSize ?? 1}em`,
      preview,
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;

    const ariaId = `${config.uuid}:preferredVenue`;
    const possibilityAriaId = `${config.uuid}:possibilityVenue`;
    // label
    const labelProps: Components.FormLabelProps = {
      htmlFor: ariaId,
      required,
    };
    const possibilityProps: Components.FormLabelProps = {
      htmlFor: possibilityAriaId,
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ register, onChangePreferredVenueSelect }) => (
          <Components.FormUnit key={`preview:template:venue:${uuid}`}>
            <Components.FormLabel {...labelProps}>
              {labelConfig[`text.${langCode}`] && ContentsHandler.toText(labelConfig)}
            </Components.FormLabel>
            <Components.Select
              {...(!preview && register
                ? register(`${config['venue.apply_form_no']}` as const, {
                    onChange(e) {
                      onChangePreferredVenueSelect(e);
                    },
                  })
                : {})}
              required={required}
              id={ariaId}
            >
              <option value="">選択してください</option>
              {config.labelAndValues?.map(({ label, value }, i) => (
                <option key={`preview:template:venue:${uuid}:option:${i}:${value}`} value={value}>
                  {label}
                </option>
              ))}
            </Components.Select>
            {!possibilityExclude && (
              <Components.FormUnit className="mt-[10px] mb-[10px]">
                <Components.FormLabel {...possibilityProps}>
                  {possibilityConfig[`text.${langCode}`] &&
                    ContentsHandler.toText(possibilityConfig)}
                </Components.FormLabel>
                <div className={`flex flex-col gap-[5px]`}>
                  {config.labelAndValues?.map(({ label, value }, i) => (
                    <Components.Checkbox
                      key={`preview:template:venue:${uuid}:possibility:option:${i}:${value}`}
                      value={value}
                      {...(!preview && register
                        ? register(`${config['possibility.apply_form_no']}.labelAndValues` as const)
                        : {})}
                      id={possibilityAriaId}
                    >
                      {label}
                    </Components.Checkbox>
                  ))}
                </div>
              </Components.FormUnit>
            )}
            {!noteExclude && ContentsHandler.toText(noteConfig)}
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toTemplateAddress(
    config: Types.PreviewTemplateAddress | Types.TemplateAddress
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const {
      uuid,
      preview,
      isGlobal,
      'postcode.exclude': postcodeExclude,
      'postcode.required': postcodeRequired,
      'postcode.label.color': postcodeLabelColor,
      'prefecture.exclude': prefectureExclude,
      'prefecture.required': prefectureRequired,
      'prefecture.label.color': prefectureLabelColor,
      'prefecture.labelAndValues': prefectureLabelAndValues,
      'city.exclude': cityExclude,
      'city.required': cityRequired,
      'city.label.color': cityLabelColor,
      'others.exclude': othersExclude,
      'others.required': othersRequired,
      'others.label.color': othersLabelColor,
    } = config;
    const postcodeLabelText = `${config[`postcode.label.text.${langCode}`] ?? ''}`;
    const prefectureLabelText = `${config[`prefecture.label.text.${langCode}`] ?? ''}`;
    const cityLabelText = `${config[`city.label.text.${langCode}`] ?? ''}`;
    const othersLabelText = `${config[`others.label.text.${langCode}`] ?? ''}`;

    const ariaIdPostcode = `${config.uuid}:postcode`;
    const ariaIdPrefecture = `${config.uuid}:prefecture`;
    const ariaIdCity = `${config.uuid}:city`;
    const ariaIdOthers = `${config.uuid}:others`;
    // label
    const labelPostcodeProps: Components.FormLabelProps = {
      htmlFor: ariaIdPostcode,
      required: postcodeRequired,
    };
    const labelPrefectureProps: Components.FormLabelProps = {
      htmlFor: ariaIdPrefecture,
      required: prefectureRequired,
    };
    const labelCityProps: Components.FormLabelProps = {
      htmlFor: ariaIdCity,
      required: cityRequired,
    };
    const labelOthersProps: Components.FormLabelProps = {
      htmlFor: ariaIdOthers,
      required: othersRequired,
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ t, register, onClickTemplateAddressSearch }) => (
          <React.Fragment key={`preview:template:address:${uuid}`}>
            {!postcodeExclude && !isGlobal && (
              <Components.FormUnit>
                <Components.FormLabel {...labelPostcodeProps}>
                  {postcodeLabelText &&
                    ContentsHandler.toText({
                      [`text.${langCode}`]: postcodeLabelText,
                      color: postcodeLabelColor,
                      preview,
                    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.FormLabel>
                <div className="flex flex-row gap-x-[10px]">
                  <Components.Input
                    className="basis-[20em] grow-0"
                    type="number"
                    {...(!preview && register
                      ? register(`${config['postcode.apply_form_no']}` as const)
                      : {})}
                    {...(preview ? { readOnly: true } : {})}
                    placeholder={config[`postcode.placeholder.${langCode}`]}
                    required={postcodeRequired}
                    id={ariaIdPostcode}
                  />
                  <Components.Button
                    {...(preview
                      ? {}
                      : {
                          onClick: (e) =>
                            onClickTemplateAddressSearch?.(e, {
                              postcode: !preview ? (config['postcode.apply_form_no'] ?? NaN) : NaN,
                              prefecture: !preview
                                ? (config['prefecture.apply_form_no'] ?? NaN)
                                : NaN,
                              city: !preview ? (config['city.apply_form_no'] ?? NaN) : NaN,
                              others: !preview ? (config['others.apply_form_no'] ?? NaN) : NaN,
                            }),
                        })}
                  >
                    {t('CH_002')}
                  </Components.Button>
                </div>
              </Components.FormUnit>
            )}
            {!prefectureExclude && (
              <Components.FormUnit className="mt-[10px]">
                <Components.FormLabel {...labelPrefectureProps}>
                  {prefectureLabelText &&
                    ContentsHandler.toText({
                      [`text.${langCode}`]: isGlobal ? t('CH_014') : prefectureLabelText,
                      color: prefectureLabelColor,
                      preview,
                    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.FormLabel>
                {isGlobal ? (
                  <Components.Input
                    maxLength={50}
                    {...(!preview && register
                      ? register(`${config['prefecture.apply_form_no']}` as const)
                      : {})}
                    required={prefectureRequired}
                    id={ariaIdPrefecture}
                  />
                ) : (
                  <Components.Select
                    {...(!preview && register
                      ? register(`${config['prefecture.apply_form_no']}` as const)
                      : {})}
                    required={prefectureRequired}
                    id={ariaIdPrefecture}
                  >
                    {!preview &&
                      prefectureLabelAndValues?.map(({ label, value }) => (
                        <option
                          key={`preview:template:address:${uuid}:select-prefecture:${value}`}
                          value={value}
                        >
                          {label}
                        </option>
                      ))}
                  </Components.Select>
                )}
              </Components.FormUnit>
            )}
            {!cityExclude && (
              <Components.FormUnit className="mt-[10px]">
                <Components.FormLabel {...labelCityProps}>
                  {cityLabelText &&
                    ContentsHandler.toText({
                      [`text.${langCode}`]: isGlobal ? t('CH_013') : cityLabelText,
                      color: cityLabelColor,
                      preview,
                    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.FormLabel>

                <Components.Input
                  placeholder={config[`city.placeholder.${langCode}`]}
                  {...(!preview && register
                    ? register(`${config['city.apply_form_no']}` as const)
                    : {})}
                  required={cityRequired}
                  id={ariaIdCity}
                />
              </Components.FormUnit>
            )}
            {!othersExclude && (
              <Components.FormUnit className="mt-[10px]">
                <Components.FormLabel {...labelOthersProps}>
                  {othersLabelText &&
                    ContentsHandler.toText({
                      [`text.${langCode}`]: othersLabelText,
                      color: othersLabelColor,
                      preview,
                    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.FormLabel>
                <Components.Input
                  placeholder={config[`others.placeholder.${langCode}`]}
                  {...(!preview && register
                    ? register(`${config['others.apply_form_no']}` as const)
                    : {})}
                  required={othersRequired}
                  id={ariaIdOthers}
                />
              </Components.FormUnit>
            )}
          </React.Fragment>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toTemplateMembers(
    config: Types.PreviewTemplateMembers | Types.TemplateMembers
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const {
      uuid,
      preview,
      isGlobal,
      'input1.exclude': input1Exclude,
      'input1.label.color': input1LabelColor,
      'input2.exclude': input2Exclude,
      'input2.label.color': input2LabelColor,
      'input3.exclude': input3Exclude,
      'input3.label.color': input3LabelColor,
      'input4.exclude': input4Exclude,
      'input4.label.color': input4LabelColor,
      'input5.exclude': input5Exclude,
      'input5.label.color': input5LabelColor,
      'input6.exclude': input6Exclude,
      'input6.label.color': input6LabelColor,
    } = config;
    const isTopName = input1Exclude ? false : true;
    const isTopYomigana = isTopName ? false : input2Exclude && !isGlobal ? false : true;
    const isTopAgeOrGender =
      isTopName || isTopYomigana ? false : input3Exclude && input4Exclude ? false : true;
    const isTopPart =
      isTopName || isTopYomigana || isTopAgeOrGender ? false : input5Exclude ? false : true;
    const isTopMemberType =
      isTopName || isTopYomigana || isTopAgeOrGender || isTopPart
        ? false
        : input6Exclude
          ? false
          : true;
    const topClassName = `mt-[10px]`;
    const isJa = langCode === '0';

    const onFocusOutMemberNum: (
      onClick: Types.ContentsHandlerContextValue['onChangeTemplateMembersNum']
    ) => React.FocusEventHandler<HTMLInputElement> = (onChangeTemplateMembersNum) => (e) => {
      let num = e.currentTarget.value ? parseInt(e.currentTarget.value) : 2;
      if (20 < num) {
        // 最大値以上の入力をした場合
        num = 20;
        e.currentTarget.value = '20';
      }
      if (1 >= num || e.currentTarget.value == '') {
        // 0以下は自動的に最小値の入力
        num = 2;
        e.currentTarget.value = '2';
      }
      e.preventDefault();
      onChangeTemplateMembersNum(num);
    };
    const Member = ({
      id,
      index,
      applyType,
    }: {
      id: string;
      index: number;
      applyType: 'SOLO' | 'GROUP';
    }) => {
      const ariaIdInput1 = `${config.uuid}:input1`;
      const ariaIdInput2 = `${config.uuid}:input2`;
      const ariaIdInput3 = `${config.uuid}:input3`;
      const ariaIdInput4 = `${config.uuid}:input4`;
      const ariaIdInput5 = `${config.uuid}:input5`;
      const ariaIdInput6 = `${config.uuid}:input6`;
      // label
      const labelInput1Props: Components.FormLabelProps = {
        htmlFor: ariaIdInput1,
        required: true,
      };
      const labelInput2Props: Components.FormLabelProps = {
        htmlFor: ariaIdInput2,
        required: true,
      };
      const labelInput3Props: Components.FormLabelProps = {
        htmlFor: ariaIdInput3,
        required: true,
      };
      const labelInput4Props: Components.FormLabelProps = {
        htmlFor: ariaIdInput4,
        required: true,
      };
      const labelInput5Props: Components.FormLabelProps = {
        htmlFor: ariaIdInput5,
        required: true,
      };
      const labelInput6Props: Components.FormLabelProps = {
        htmlFor: ariaIdInput6,
        required: true,
      };
      return (
        <ContentsHandlerContext.Consumer>
          {({ t, templateMembersIds, onClickTemplateMembersDelete, register }) => (
            <section className="@container relative p-[5px] mt-[5px] border-solid border-[1px] border-[cce4f5]">
              <div className="@[715px]:flex @[715px]:gap-x-[10px]">
                {!input1Exclude && (
                  <Components.FormUnit
                    className={`basis-[calc(50%-5px)] shrink-0 grow-0 ${isTopName ? topClassName : ''}`}
                  >
                    <Components.FormLabel {...labelInput1Props}>
                      {ContentsHandler.toText({
                        [`text.${langCode}`]: config[`input1.label.text.${langCode}`],
                        color: input1LabelColor,
                        preview,
                      } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                    </Components.FormLabel>
                    <Components.Input
                      key={id}
                      placeholder={config[`input1.placeholder.${langCode}`]}
                      {...(!preview && register
                        ? register(`${config['apply_form_no']}.${index}.input1` as const)
                        : {})}
                      id={ariaIdInput1}
                      maxLength={40}
                      required={applyType === 'GROUP' ? true : false}
                    />
                  </Components.FormUnit>
                )}
                {!input2Exclude && !isGlobal && (
                  <Components.FormUnit
                    className={`basis-[calc(50%-5px)] shrink-0 grow-0 ${isTopYomigana ? topClassName : 'mt-[10px]'}`}
                  >
                    <Components.FormLabel {...labelInput2Props}>
                      {ContentsHandler.toText({
                        [`text.${langCode}`]: config[`input2.label.text.${langCode}`],
                        color: input2LabelColor,
                        preview,
                      } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                    </Components.FormLabel>
                    <Components.Input
                      key={id}
                      placeholder={config[`input2.placeholder.${langCode}`]}
                      pattern={Consts.KANA_REGEX.source}
                      {...(!preview && register
                        ? register(`${config['apply_form_no']}.${index}.input2` as const)
                        : {})}
                      id={ariaIdInput2}
                      maxLength={40}
                      required={applyType === 'GROUP' ? true : false}
                    />
                  </Components.FormUnit>
                )}
              </div>
              {(!input3Exclude || !input4Exclude) && (
                <div
                  className={`flex flex-row gap-x-[10px] ${isTopAgeOrGender ? topClassName : 'mt-[10px]'}`}
                >
                  {!input3Exclude && (
                    <Components.FormUnit className={'basis-[calc(50%-5px)] shrink-0 grow-0'}>
                      <Components.FormLabel {...labelInput3Props}>
                        {ContentsHandler.toText({
                          [`text.${langCode}`]: config[`input3.label.text.${langCode}`],
                          color: input3LabelColor,
                          preview,
                        } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                      </Components.FormLabel>
                      <Components.Input
                        key={id}
                        placeholder={config[`input3.placeholder.${langCode}`]}
                        {...(!preview && register
                          ? register(`${config['apply_form_no']}.${index}.input3` as const)
                          : {})}
                        id={ariaIdInput3}
                        maxLength={3}
                        required={applyType === 'GROUP' ? true : false}
                      />
                    </Components.FormUnit>
                  )}
                  {!input4Exclude && (
                    <Components.FormUnit className={'basis-[calc(50%-5px)] shrink-0 grow-0'}>
                      <Components.FormLabel {...labelInput4Props}>
                        {ContentsHandler.toText({
                          [`text.${langCode}`]: config[`input4.label.text.${langCode}`],
                          color: input4LabelColor,
                          preview,
                        } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                      </Components.FormLabel>

                      <Components.Select
                        key={id}
                        className="w-[100%]"
                        {...(!preview && register
                          ? register(`${config['apply_form_no']}.${index}.input4` as const)
                          : {})}
                        defaultValue={Consts.TEMPLATE_MEMBERS_GENDER_VALUES.NONE}
                        id={ariaIdInput4}
                        required={applyType === 'GROUP' ? true : false}
                      >
                        {Consts.TEMPLATE_MEMBERS_GENDER_OPTIONS.map((value, i) => (
                          <option
                            key={`preview:template:member:${uuid}:select-input4:${i}`}
                            value={t(value)}
                          >
                            {t(value)}
                          </option>
                        ))}
                      </Components.Select>
                    </Components.FormUnit>
                  )}
                </div>
              )}
              <div className="@[715px]:flex @[715px]:gap-x-[10px]">
                {!input5Exclude && (
                  <Components.FormUnit
                    className={`basis-[calc(50%-5px)] shrink-0 grow-0 ${isTopPart ? topClassName : 'mt-[10px]'}`}
                  >
                    <Components.FormLabel {...labelInput5Props}>
                      {ContentsHandler.toText({
                        [`text.${langCode}`]: config[`input5.label.text.${langCode}`],
                        color: input5LabelColor,
                        preview,
                      } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                    </Components.FormLabel>
                    <Components.Input
                      key={id}
                      placeholder={config[`input5.placeholder.${langCode}`]}
                      {...(!preview && register
                        ? register(`${config['apply_form_no']}.${index}.input5` as const)
                        : {})}
                      id={ariaIdInput5}
                      maxLength={40}
                      required={applyType === 'GROUP' ? true : false}
                    />
                  </Components.FormUnit>
                )}
                {!input6Exclude && (
                  <Components.FormUnit
                    className={`basis-[calc(50%-5px)] shrink-0 grow-0 ${isTopMemberType ? topClassName : 'mt-[10px]'}`}
                  >
                    <Components.FormLabel {...labelInput6Props}>
                      {ContentsHandler.toText({
                        [`text.${langCode}`]: config[`input6.label.text.${langCode}`],
                        color: input6LabelColor,
                        preview,
                      } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                    </Components.FormLabel>

                    <Components.Select
                      key={id}
                      className="w-[100%]"
                      defaultValue={Consts.TEMPLATE_MEMBERS_MEMBER_TYPE_VALUES.SUPPORT_MEMBERS}
                      {...(!preview && register
                        ? register(`${config['apply_form_no']}.${index}.input6` as const)
                        : {})}
                      id={ariaIdInput6}
                      required={applyType === 'GROUP' ? true : false}
                    >
                      {Consts.TEMPLATE_MEMBERS_MEMBER_TYPE_OPTIONS.map((value, i) => (
                        <option
                          key={`preview:template:member:${uuid}:select-input6:${i}`}
                          value={t(value)}
                        >
                          {t(value)}
                        </option>
                      ))}
                    </Components.Select>
                  </Components.FormUnit>
                )}
              </div>
            </section>
          )}
        </ContentsHandlerContext.Consumer>
      );
    };

    return (
      <ContentsHandlerContext.Consumer>
        {({
          t,
          applyType,
          templateMembersIds,
          onChangeApplyType,
          onChangeTemplateMembersNum,
          register,
          hasErrorInMemberConsent,
        }) => (
          <React.Fragment key={`preview:template:address:${uuid}`}>
            <Components.FormUnit className={'mb-[10px]'}>
              <Components.FormLabel>
                {ContentsHandler.toText({
                  [`text.${langCode}`]: t('CH_020'),
                  preview,
                } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
              </Components.FormLabel>
              <Components.Select
                {...(!preview && register
                  ? register(`${config['apply_form_no']}.0.apply_type` as const)
                  : {})}
                onChange={(e) => onChangeApplyType(e)}
                defaultValue={Consts.TEMPLATE_MEMBERS_APPLY_TYPE_VALUES.GROUP}
              >
                {Consts.TEMPLATE_MEMBERS_APPLY_TYPE_OPTIONS.map((value, i) => (
                  <option
                    key={`preview:template:member:${uuid}:select-applytype:${i}`}
                    value={t(value)}
                  >
                    {t(value)}
                  </option>
                ))}
              </Components.Select>
            </Components.FormUnit>
            <div className={`${!preview && applyType === 'SOLO' ? 'hidden' : ''}`}>
              {!isGlobal && (
                <Components.Checkbox
                  className="!opacity-100 font-bold"
                  {...(!preview && register
                    ? register(`${config['apply_form_no']}.0.consent` as const)
                    : {})}
                >
                  {ContentsHandler.toText({
                    [`text.${langCode}`]: t('CH_019'),
                    preview,
                  } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.Checkbox>
              )}
              {!preview && hasErrorInMemberConsent && (
                <p className="text-[--c-error] mt-[0.5em]">
                  {isJa
                    ? `メンバーが入力されています。同意の確認を行なってください`
                    : `Members have been entered. Please confirm your consent.`}
                </p>
              )}

              <Components.FormUnit className={'mt-[10px]'}>
                <Components.FormLabel>
                  {ContentsHandler.toText({
                    [`text.${langCode}`]: t('CH_021'),
                    preview,
                  } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.FormLabel>
                <Components.Input
                  type="number"
                  pattern={Consts.NUMERIC_REGEX.source}
                  min={preview ? 1 : 2}
                  max={Consts.TEMPLATE_MEMBERS_MAX_NUM}
                  {...(!preview && register
                    ? register(`${config['apply_form_no']}.0.member_num` as const, {
                        value: templateMembersIds.length ?? 2,
                      })
                    : {})}
                  onBlur={onFocusOutMemberNum(onChangeTemplateMembersNum)}
                  required={applyType === 'GROUP' ? true : false}
                />
              </Components.FormUnit>
              <div className="flex flex-col gap-y-[16px]">
                {templateMembersIds.map((id, i) => (
                  <Member key={i + id} id={id} index={i} applyType={applyType} />
                ))}
              </div>
              <Components.FormUnit className={'mt-[10px] font-bold"'}>
                <Components.FormLabel>
                  {ContentsHandler.toText({
                    [`text.${langCode}`]: t('CH_022'),
                    preview,
                  } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.FormLabel>
                <Components.Checkbox
                  className="!opacity-100 font-bold"
                  required={applyType === 'GROUP' ? true : false}
                >
                  {ContentsHandler.toText({
                    [`text.${langCode}`]: t('CH_023'),
                    preview,
                  } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>)}
                </Components.Checkbox>
              </Components.FormUnit>
            </div>
          </React.Fragment>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toTemplateConsent(
    config: Types.PreviewTemplateConsent | Types.TemplateConsent
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const textProp = `text.${langCode}` as const;
    const labelConfig = {
      preview: config.preview,
      [textProp]: config[`label.text.${langCode}`],
      color: config['label.color'],
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;
    const { preview, required } = config;
    const accept = '.jpeg,.jpg,.png,.pdf,.heic';
    const ariaId = config.uuid;
    // label
    const labelProps: Components.FormLabelProps = {
      htmlFor: ariaId,
      required,
    };
    // input
    const props: React.InputHTMLAttributes<HTMLInputElement> = {
      ...(preview ? { readOnly: true } : {}),
      required,
      accept,
      id: ariaId,
    };
    const isJa = langCode === '0';
    return (
      <ContentsHandlerContext.Consumer>
        {({ register, hasErrorConsentFiles }) => (
          <Components.FormUnit>
            <Components.FormLabel {...labelProps}>
              {labelConfig[textProp] && ContentsHandler.toText(labelConfig)}
            </Components.FormLabel>
            <Components.FileInput
              label={config[`placeholder.${langCode}`]}
              multiple
              {...(!preview && register ? register(`${config['apply_form_no']}` as const) : {})}
              {...props}
            />
            {!preview && hasErrorConsentFiles && (
              <p className="text-[--c-error] mt-[0.5em]">
                {isJa ? `選択できるファイルは最大５つです。` : `You can select up to five files.`}
              </p>
            )}
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toParallelInput(
    config: Types.PreviewParallelInput | Types.ParallelInput
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const maxLength = 50;
    const { preview } = config;

    const ariaIdLeft = `${config.uuid}:left`;
    const ariaIdRight = `${config.uuid}:right`;
    // label
    const labelLeftProps: Components.FormLabelProps = {
      htmlFor: ariaIdLeft,
      required: config['left.required'],
    };
    const leftLabelConfig = {
      preview: config.preview,
      [`text.${langCode}` as const]: config[`left.label.text.${langCode}`],
      color: config['left.label.color'],
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;
    const labelRightProps: Components.FormLabelProps = {
      htmlFor: ariaIdRight,
      required: config['right.required'],
    };
    const rightLabelConfig = {
      preview: config.preview,
      [`text.${langCode}` as const]: config[`right.label.text.${langCode}`],
      color: config['right.label.color'],
    } as Omit<Types.PreviewText | Types.Text, 'uuid' | 'element'>;
    // input
    const leftProps: React.InputHTMLAttributes<HTMLInputElement> = {
      type: 'text',
      placeholder: config[`left.placeholder.${langCode}`],
      maxLength,
      ...(preview ? { readOnly: true } : {}),
      required: config['left.required'],
      id: ariaIdLeft,
    };
    const rightProps: React.InputHTMLAttributes<HTMLInputElement> = {
      type: 'text',
      placeholder: config[`right.placeholder.${langCode}`],
      maxLength,
      ...(preview ? { readOnly: true } : {}),
      required: config['right.required'],
      id: ariaIdRight,
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ register }) => (
          <div className="flex flex-row gap-x-[10px] items-end">
            <Components.FormUnit>
              <Components.FormLabel {...labelLeftProps}>
                {leftLabelConfig[`text.${langCode}`] && ContentsHandler.toText(leftLabelConfig)}
              </Components.FormLabel>
              <Components.Input
                {...(!preview && register
                  ? register(`${config['left.apply_form_no']}` as const)
                  : {})}
                {...leftProps}
              />
            </Components.FormUnit>
            <Components.FormUnit>
              <Components.FormLabel {...labelRightProps}>
                {rightLabelConfig[`text.${langCode}`] && ContentsHandler.toText(rightLabelConfig)}
              </Components.FormLabel>
              <Components.Input
                {...(!preview && register
                  ? register(`${config['right.apply_form_no']}` as const)
                  : {})}
                {...rightProps}
              />
            </Components.FormUnit>
          </div>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toDropdownList(
    config: Types.PreviewDropdownList | Types.DropdownList
  ): React.ReactNode {
    const { langCode } = ContentsHandler;
    const { uuid, preview, required, labelAndValues = [], etcInput } = config;
    const labelConfig = ContentsHandler.extractLabelConfig(config);
    const ariaId = config.uuid;
    const labelProps: Components.FormLabelProps = {
      htmlFor: ariaId,
      className: 'pt-2',
      required,
    };
    const isJa = langCode === '0';
    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
      type: 'text',
      placeholder: isJa ? 'その他' : 'Others',
      maxLength: 50,
      ...(preview ? { readOnly: true } : {}),
      id: `${ariaId}.etc`,
    };

    return (
      <ContentsHandlerContext.Consumer>
        {({ register }) => (
          <Components.FormUnit>
            <Components.FormLabel {...labelProps}>
              {labelConfig[`text.${langCode}`] && ContentsHandler.toText(labelConfig)}
            </Components.FormLabel>
            <div>
              <Components.Select
                className="w-fit"
                {...(!preview && register
                  ? register(`${config['apply_form_no']}.labelAndValues` as const)
                  : {})}
                required={required}
              >
                {labelAndValues.map(
                  ({ [`label.${langCode}` as const]: label, value = label }, i) => (
                    <option
                      key={`preview:${uuid}:dropdown-list${i}`}
                      value={value}
                      {...(i === 0 && required ? { disabled: true } : {})}
                    >
                      {label}
                    </option>
                  )
                )}
              </Components.Select>
              {etcInput && (
                <Components.Input
                  className="mt-[10px]"
                  {...(!preview && register
                    ? register(`${config['apply_form_no']}.etc` as const)
                    : {})}
                  {...inputProps}
                />
              )}
            </div>
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  public static toCategory(config: Types.PreviewCategory | Types.Category): React.ReactNode {
    const { langCode } = ContentsHandler;
    const { uuid, preview, required, selectable, labelAndValues = [], direction = 'row' } = config;
    const labelConfig = ContentsHandler.extractLabelConfig(config);

    console.log(selectable);

    const ariaId = config.uuid;
    const isJa = langCode === '0';
    // label
    const labelProps: Components.FormLabelProps = {
      id: ariaId,
      required,
      className: 'pt-2',
    };
    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
      type: 'text',
      placeholder: isJa ? 'その他' : 'Others',
      maxLength: 50,
      ...(preview ? { readOnly: true } : {}),
      id: `${ariaId}.etc`,
    };
    const itemsContainerClassName = `flex ${direction === 'row' ? 'flex-row flex-wrap' : 'flex-col'} gap-[10px]`;
    // ios用
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
      const element = event.currentTarget;
      if (element.type !== 'checkbox') {
        return;
      }
      try {
        const rect = element.getBoundingClientRect();
        if (1 < rect.top && rect.bottom < window.innerHeight) return;

        element.scrollIntoView({
          block: 'center',
          behavior: 'auto',
        });
      } catch (e) {
        console.error(e);
      }
    };
    return (
      <ContentsHandlerContext.Consumer>
        {({ register, isMoreSelected, onChangeCheckParts }) => (
          <Components.FormUnit>
            <Components.FormLabel {...labelProps}>
              {labelConfig[`text.${langCode}`] && ContentsHandler.toText(labelConfig)}
            </Components.FormLabel>
            <div className={itemsContainerClassName}>
              {labelAndValues.map(({ [`label.${langCode}` as const]: label, value = label }, i) => (
                <Components.SelectableButton
                  key={`preview:${uuid}:check${i}`}
                  value={value}
                  {...(isIOS ? { onFocus: onFocus } : {})}
                  {...(!preview && register
                    ? register(`${config.apply_form_no}.labelAndValues` as const, {
                        onChange: (e) =>
                          onChangeCheckParts(
                            e,
                            parseInt(selectable ?? '-1'),
                            !preview ? `${config.apply_form_no}.labelAndValues` : '',
                            required
                          ),
                      })
                    : {})}
                  required={required}
                >
                  {label}
                </Components.SelectableButton>
              ))}
            </div>
            <Components.Input
              className="mt-[10px]"
              {...(!preview && register ? register(`${config['apply_form_no']}.etc` as const) : {})}
              {...inputProps}
            />
            {!preview && isMoreSelected?.includes(`${config.apply_form_no}.labelAndValues`) && (
              <p className="text-[--c-error] mt-[0.5em]">
                {isJa
                  ? `最大選択数は${selectable}個です。`
                  : `The maximum number of selections is ${selectable}.`}
              </p>
            )}
          </Components.FormUnit>
        )}
      </ContentsHandlerContext.Consumer>
    );
  }

  private static extractLabelConfig(
    config:
      | Types.PreviewInput
      | Types.Input
      | Types.PreviewInputLink
      | Types.InputLink
      | Types.PreviewInputFile
      | Types.InputFile
      | Types.PreviewCheck
      | Types.Check
      | Types.PreviewDropdownList
      | Types.DropdownList
      | Types.PreviewCategory
      | Types.Category
  ) {
    const labelConfig = { preview: config.preview } as Omit<
      Types.PreviewText | Types.Text,
      'uuid' | 'element'
    >;
    //TS2590: Expression produces a union type that is too complex to represent.
    //型が複雑すぎる？ためエラーになるので抑制
    //@ts-ignore
    for (const key in config) {
      if (key.startsWith('label.')) {
        const labelConfigKey = key.replace(/^.*label\./, '') as Types.EditableTextProps;
        labelConfig[labelConfigKey] = config[key as Types.EditableInputLabelProps];
      }
    }
    return labelConfig;
  }

  public static toJSX() {}

  /**
   * プレビューを応募フォーム情報に変換
   * @param previewItems
   * @returns
   */
  public static async toApplyFormList(
    previewItems: Types.PreviewItem[],
    onFileUpload: (file: File) => Promise<string>,
    opts?: ToApplyFormListOpts
  ) {
    const {
      /* 1~3はレイアウト情報で使用するため、4から開始 */
      applyFormNoOffset = Consts.RESERVED_APPLY_FORM_IDS.footer,
    } = opts ?? {};
    const targetPreviewItems = await Promise.all(
      previewItems
        // Gohstを除外
        .filter((item) => item.element !== 'gohst')
        .map(async (item) => {
          if (item.element === 'image') {
            // 画像が選択されていない状態で登録可能であればnullではなくitemを返すようにする
            if (!item.src) {
              return null;
            }
            const fileObj = FileHandler.get(item.src);
            // srcが指定されており、FileHandlerで取得できない場合既にアップロード済みの画像とみなす
            if (!fileObj) {
              return item;
            }

            const registeredFileKey = await onFileUpload(fileObj);
            return {
              ...item,
              src: registeredFileKey,
            } as Types.PreviewImage;
          } else {
            return item as Exclude<Types.PreviewItem, Types.PreviewGohst | Types.PreviewImage>;
          }
        })
    );
    type ApplyFormItem = Omit<Types.ApplyForm, 'template_id' | 'examination_no'>;
    const applyFormItems: ApplyFormItem[] = [];
    let sortNo = 1 + applyFormNoOffset;
    (
      targetPreviewItems.filter((item) => item !== null) as Exclude<
        Types.PreviewItem,
        Types.PreviewGohst
      >[]
    ).forEach((item) => {
      let langCodes: string[] = [];
      switch (item.element) {
        case 'template:consent': {
          // lebel.text,placeholder の langCode 部分を抽出
          const reg = /^(placeholder|label\.text)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const input: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '20',
            sort_no: sortNo,
            required: item.required ? '1' : '0',
            styles: {
              color: item['label.color'],
              // 'font-weight': item['label.formats']?.includes('bold') ? 'bold' : undefined,
              // 'font-style': item['label.formats']?.includes('italic') ? 'italic' : undefined,
              // 'text-decoration': item['label.formats']?.includes('underlined') ? 'underline' : undefined,
            },
          };
          for (const langCode of langCodes) {
            input[`question.${langCode}`] = item[`label.text.${langCode}`] ?? '';
            input[`place_holder.${langCode}`] = item[`placeholder.${langCode}`] ?? '';
          }
          applyFormItems.push(input);
          break;
        }
        case 'template:artist': {
          // name.lebel.text,kana.lebel.text,name.placeholder,kana.placeholder の langCode 部分を抽出
          const reg = /^(name|kana)\.(label\.text|placeholder)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const artistName: ApplyFormItem = {
            apply_form_no: item['name.apply_form_no']!,
            format: '50',
            sort_no: sortNo++,
            // TODO:「アカウント登録時氏名と同じ」が未実装のため暫定固定値
            sync: true,
            required: item['name.required'] ? Consts.REQUIRED.TRUE : Consts.REQUIRED.FALSE,
            styles: {
              color: item['name.label.color'],
            },
            general1: item['showAutoFill'] ? Consts.SHOW.TRUE : Consts.SHOW.FALSE,
          };
          for (const langCode of langCodes) {
            artistName[`question.${langCode}`] = item[`name.label.text.${langCode}`];
            artistName[`place_holder.${langCode}`] = item[`name.placeholder.${langCode}`] ?? '';
          }
          const artistKana: ApplyFormItem = {
            apply_form_no: item['kana.apply_form_no']!,
            format: '51',
            sort_no: sortNo,
            // TODO:「アカウント登録時氏名と同じ」が未実装のため暫定固定値
            sync: true,
            required: item['kana.required'] ? Consts.REQUIRED.TRUE : Consts.REQUIRED.FALSE,
            styles: {
              color: item['kana.label.color'],
            },
          };
          for (const langCode of langCodes) {
            artistKana[`question.${langCode}`] = item[`kana.label.text.${langCode}`];
            artistKana[`place_holder.${langCode}`] = item[`kana.placeholder.${langCode}`] ?? '';
          }
          applyFormItems.push(artistName, artistKana);
          break;
        }
        case 'template:venue': {
          // label.text,note.text の langCode 部分を抽出
          const reg = /^(label|note)\.text\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const venue: ApplyFormItem = {
            apply_form_no: item['venue.apply_form_no']!,
            format: '52',
            sort_no: sortNo,
            required: item.required ? '1' : '0',
            styles: {
              color: item['label.color'],
            },
            general1: String(item.examination_no),
            general2: item.displayNameType,
          };
          const venuePossibility: ApplyFormItem = {
            apply_form_no: item['possibility.apply_form_no']!,
            format: '58',
            sort_no: sortNo + 1,
            styles: {
              color: item['possibility.color'],
            },
            general2: item.displayNameType,
          };
          const venueNote: ApplyFormItem = {
            apply_form_no: item['note.apply_form_no']!,
            format: '53',
            sort_no: sortNo + 2,
            styles: {
              color: item['note.color'],
              'font-size': `${item['note.size'] ?? ''}`,
            },
          };
          for (const langCode of langCodes) {
            venue[`question.${langCode}`] = item[`label.text.${langCode}`];
            venuePossibility[`question.${langCode}`] = item[`possibility.text.${langCode}`];
            venueNote[`general1.${langCode}`] = item[`note.text.${langCode}`];
          }
          applyFormItems.push(venue);
          if (!item['possibility.exclude']) {
            applyFormItems.push(venuePossibility);
            sortNo++;
          }
          if (!item['note.exclude']) {
            applyFormItems.push(venueNote);
            sortNo++;
          }
          break;
        }
        case 'template:address': {
          // postcode.label.text,city.label.text,others.label.text,postcode.placeholder,city.placeholder,others.placeholder の langCode 部分を抽出
          const reg = /^(postcode|city|others)\.(label\.text|placeholder)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          let included = 0;
          if (!item['postcode.exclude']) {
            const postcode: ApplyFormItem = {
              apply_form_no: item['postcode.apply_form_no']!,
              format: '11',
              sort_no: sortNo + included,
              required: item['postcode.required'] ? '1' : '0',
              styles: {
                color: item['postcode.label.color'],
              },
            };
            for (const langCode of langCodes) {
              postcode[`question.${langCode}`] = item[`postcode.label.text.${langCode}`];
              postcode[`place_holder.${langCode}`] = item[`postcode.placeholder.${langCode}`];
            }
            applyFormItems.push(postcode);
            included++;
          }
          if (!item['prefecture.exclude']) {
            const prefecture: ApplyFormItem = {
              apply_form_no: item['prefecture.apply_form_no']!,
              format: '12',
              sort_no: sortNo + included,
              required: item['prefecture.required'] ? '1' : '0',
              styles: {
                color: item['prefecture.label.color'],
              },
            };
            for (const langCode of langCodes) {
              prefecture[`question.${langCode}`] = item[`prefecture.label.text.${langCode}`];
            }
            applyFormItems.push(prefecture);
            included++;
          }
          if (!item['city.exclude']) {
            const city: ApplyFormItem = {
              apply_form_no: item['city.apply_form_no']!,
              format: '13',
              sort_no: sortNo + included,
              required: item['city.required'] ? '1' : '0',
              styles: {
                color: item['city.label.color'],
              },
            };
            for (const langCode of langCodes) {
              city[`question.${langCode}`] = item[`city.label.text.${langCode}`];
              city[`place_holder.${langCode}`] = item[`city.placeholder.${langCode}`];
            }
            applyFormItems.push(city);
            included++;
          }
          if (!item['others.exclude']) {
            const others: ApplyFormItem = {
              apply_form_no: item['others.apply_form_no']!,
              format: '14',
              sort_no: sortNo + included,
              required: item['others.required'] ? '1' : '0',
              styles: {
                color: item['others.label.color'],
              },
            };
            for (const langCode of langCodes) {
              others[`question.${langCode}`] = item[`others.label.text.${langCode}`];
              others[`place_holder.${langCode}`] = item[`others.placeholder.${langCode}`];
            }
            applyFormItems.push(others);
          }
          sortNo += included;
          break;
        }
        case 'template:members': {
          const reg = /^(input1|input2|input4)\.(label\.text|placeholder)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const joinedGeneral1 = [
            item['input1.exclude'] ? '0' : '1',
            item['input2.exclude'] ? '0' : '1',
            item['input3.exclude'] ? '0' : '1',
            item['input4.exclude'] ? '0' : '1',
            item['input5.exclude'] ? '0' : '1',
            item['input6.exclude'] ? '0' : '1',
          ].join(',');
          const members: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '54',
            sort_no: sortNo++,
            styles: {
              color: item['input1.label.color'],
            },
            general1: joinedGeneral1,
          };
          for (const langCode of langCodes) {
            members[`question.${langCode}`] = Utils.isLangCodeJa(langCode)
              ? 'メンバー情報'
              : 'Member Information';
            const joinedPlaceholder = [
              !item['input1.exclude'] ? item[`input1.placeholder.${langCode}`] : '',
              !item['input2.exclude'] ? item[`input2.placeholder.${langCode}`] : '',
              !item['input3.exclude'] ? item[`input3.placeholder.${langCode}`] : '',
              '', //性別は入力がないため常に空文字
              !item['input5.exclude'] ? item[`input5.placeholder.${langCode}`] : '',
            ].join(',');
            members[`place_holder.${langCode}`] = joinedPlaceholder;
            members[`general2.${langCode}`] = item[`consent.label.text.${langCode}`];
            const joinedTextLabel = [
              !item['input1.exclude'] ? item[`input1.label.text.${langCode}`] : '',
              !item['input2.exclude'] ? item[`input2.label.text.${langCode}`] : '',
              !item['input3.exclude'] ? item[`input3.label.text.${langCode}`] : '',
              !item['input4.exclude'] ? item[`input4.label.text.${langCode}`] : '',
              !item['input5.exclude'] ? item[`input5.label.text.${langCode}`] : '',
              !item['input6.exclude'] ? item[`input6.label.text.${langCode}`] : '',
            ].join(',');
            members[`general3.${langCode}`] = joinedTextLabel;
          }
          applyFormItems.push(members);
          break;
        }
        case 'input': {
          // lebel.text,placeholder の langCode 部分を抽出
          const reg = /^(placeholder|label\.text)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const input: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '00',
            sort_no: sortNo,
            required: item.required ? '1' : '0',
            styles: {
              color: item['label.color'],
              'font-weight': item['label.formats']?.includes('bold') ? 'bold' : undefined,
              'font-style': item['label.formats']?.includes('italic') ? 'italic' : undefined,
              'text-decoration': item['label.formats']?.includes('underlined')
                ? 'underline'
                : undefined,
            },
          };
          switch (item.type) {
            case 'file':
              input.format =
                item.fileType === 'video' ? '09' : item.fileType === 'image' ? '08' : '17';
              break;
            case 'url':
              input.format = '07';
              input.general1 = item.maxLength;
              break;
            case 'text':
            default:
              input.format = 1 <= Number(item.lineBreaks ?? '1') ? '02' : '01';
              input.general1 = item.maxLength;
              input.general2 = item.validation ?? Consts.VALIDATION.NONE;
              break;
          }
          for (const langCode of langCodes) {
            input[`question.${langCode}`] = item[`label.text.${langCode}`] ?? '';
            input[`place_holder.${langCode}`] = item[`placeholder.${langCode}`] ?? '';
          }
          if (input.format !== '00') {
            applyFormItems.push(input);
          } else {
            return true;
          }
          break;
        }
        case 'parallel-input': {
          // lebel.text,placeholder の langCode 部分を抽出
          const reg = /^(right|left)\.(placeholder|label\.text)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const leftInput: ApplyFormItem = {
            apply_form_no: item['left.apply_form_no']!,
            format: '55',
            sort_no: sortNo,
            required: item[`left.required`] ? '1' : '0',
            styles: {
              color: item['left.label.color'],
            },
          };
          const rightInput: ApplyFormItem = {
            apply_form_no: item['right.apply_form_no']!,
            format: '56',
            sort_no: sortNo + 1,
            required: item[`right.required`] ? '1' : '0',
            styles: {
              color: item['right.label.color'],
            },
          };
          for (const langCode of langCodes) {
            leftInput[`question.${langCode}`] = item[`left.label.text.${langCode}`] ?? '';
            leftInput[`place_holder.${langCode}`] = item[`left.placeholder.${langCode}`] ?? '';
            rightInput[`question.${langCode}`] = item[`right.label.text.${langCode}`] ?? '';
            rightInput[`place_holder.${langCode}`] = item[`right.placeholder.${langCode}`] ?? '';
          }

          sortNo++;
          const parallelInput = [leftInput, rightInput];
          applyFormItems.push(...parallelInput);
          break;
        }
        case 'check': {
          // label.text の langCode 部分を抽出
          const reg = /^(label\.text)\./;
          // label の langCode 部分を抽出
          const labelAndValuesReg = /^label\./;
          langCodes = [
            ...new Set([
              ...Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, '')),
              ...(item.labelAndValues ?? [])
                .map((labelAndValue) => Object.keys(labelAndValue))
                .flat()
                .filter((labelAndValue) => labelAndValuesReg.test(labelAndValue))
                .map((key) => key.replace(labelAndValuesReg, '')),
            ]),
          ];
          const check: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: Number(item.selectable ?? '-1') === 1 ? '04' : '03',
            sort_no: sortNo,
            required: item.required ? '1' : '0',
            styles: {
              color: item['label.color'],
              'font-weight': item['label.formats']?.includes('bold') ? 'bold' : undefined,
              'font-style': item['label.formats']?.includes('italic') ? 'italic' : undefined,
              'text-decoration': item['label.formats']?.includes('underlined')
                ? 'underline'
                : undefined,
            },
            general1: item.direction === 'col' ? '0' : '1',
            general2: item.selectable,
            general3: item.etcInput ? '1' : '0',
          };
          const choices: Record<string, string[]> = {};
          (item.labelAndValues ?? []).forEach((labelAndValue) => {
            for (const langCode of langCodes) {
              choices[langCode] = [
                ...(choices[langCode] ?? []),
                labelAndValue[`label.${langCode}`] ?? '',
              ];
            }
          });
          // valueは連番で採番
          check.value = (item.labelAndValues ?? []).map((_, i) => i).join(',');
          for (const langCode of langCodes) {
            check[`question.${langCode}`] = item[`label.text.${langCode}`] ?? '';
            check[`choices.${langCode}`] = (choices[langCode] ?? []).join(',');
          }
          applyFormItems.push(check);
          break;
        }
        case 'category': {
          // label.text の langCode 部分を抽出
          const reg = /^(label\.text)\./;
          // label の langCode 部分を抽出
          const labelAndValuesReg = /^label\./;
          langCodes = [
            ...new Set([
              ...Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, '')),
              ...(item.labelAndValues ?? [])
                .map((labelAndValue) => Object.keys(labelAndValue))
                .flat()
                .filter((labelAndValue) => labelAndValuesReg.test(labelAndValue))
                .map((key) => key.replace(labelAndValuesReg, '')),
            ]),
          ];
          const categories: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '57',
            sort_no: sortNo,
            required: item.required ? '1' : '0',
            styles: {
              color: item['label.color'],
              'font-weight': item['label.formats']?.includes('bold') ? 'bold' : undefined,
              'font-style': item['label.formats']?.includes('italic') ? 'italic' : undefined,
              'text-decoration': item['label.formats']?.includes('underlined')
                ? 'underline'
                : undefined,
            },
            general1: item.direction === 'col' ? '0' : '1',
            general2: item.selectable,
          };
          const choices: Record<string, string[]> = {};
          (item.labelAndValues ?? []).forEach((labelAndValue) => {
            for (const langCode of langCodes) {
              choices[langCode] = [
                ...(choices[langCode] ?? []),
                labelAndValue[`label.${langCode}`] ?? '',
              ];
            }
          });
          // valueは連番で採番
          categories.value = (item.labelAndValues ?? []).map((_, i) => i).join(',');
          for (const langCode of langCodes) {
            categories[`question.${langCode}`] = item[`label.text.${langCode}`] ?? '';
            categories[`choices.${langCode}`] = (choices[langCode] ?? []).join(',');
          }
          applyFormItems.push(categories);
          break;
        }
        case 'dropdown-list': {
          // label.text の langCode 部分を抽出
          const reg = /^(label\.text)\./;
          // label の langCode 部分を抽出
          const labelAndValuesReg = /^label\./;
          langCodes = [
            ...new Set([
              ...Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, '')),
              ...(item.labelAndValues ?? [])
                .map((labelAndValue) => Object.keys(labelAndValue))
                .flat()
                .filter((labelAndValue) => labelAndValuesReg.test(labelAndValue))
                .map((key) => key.replace(labelAndValuesReg, '')),
            ]),
          ];
          const dropDownList: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '16',
            sort_no: sortNo,
            required: item.required ? '1' : '0',
            styles: {
              color: item['label.color'],
              'font-weight': item['label.formats']?.includes('bold') ? 'bold' : undefined,
              'font-style': item['label.formats']?.includes('italic') ? 'italic' : undefined,
              'text-decoration': item['label.formats']?.includes('underlined')
                ? 'underline'
                : undefined,
            },
            general3: item.etcInput ? '1' : '0',
          };
          const choices: Record<string, string[]> = {};
          (item.labelAndValues ?? []).forEach((labelAndValue) => {
            for (const langCode of langCodes) {
              choices[langCode] = [
                ...(choices[langCode] ?? []),
                labelAndValue[`label.${langCode}`] ?? '',
              ];
            }
          });
          // valueは連番で採番
          dropDownList.value = (item.labelAndValues ?? []).map((_, i) => i).join(',');
          for (const langCode of langCodes) {
            dropDownList[`question.${langCode}`] = item[`label.text.${langCode}`] ?? '';
            dropDownList[`choices.${langCode}`] = (choices[langCode] ?? []).join(',');
          }
          applyFormItems.push(dropDownList);
          break;
        }
        case 'select-date': {
          // lebel.text の langCode 部分を抽出
          const reg = /^(label\.text)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const selectDate: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '06',
            sort_no: sortNo,
            required: item.required ? '1' : '0',
            styles: {
              color: item['label.color'],
              'font-weight': item['label.formats']?.includes('bold') ? 'bold' : undefined,
              'font-style': item['label.formats']?.includes('italic') ? 'italic' : undefined,
              'text-decoration': item['label.formats']?.includes('underlined')
                ? 'underline'
                : undefined,
            },
            general1: [...(item.exclude ?? 0).toString(2).padStart(3, '0')].join(','),
            general2: `${item['year-range']?.from ?? ''}`,
            general3: `${item['year-range']?.to ?? ''}`,
          };
          for (const langCode of langCodes) {
            selectDate[`question.${langCode}`] = item[`label.text.${langCode}`] ?? '';
          }
          applyFormItems.push(selectDate);
          break;
        }
        case 'image': {
          const image: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '40',
            sort_no: sortNo,
            styles: {
              width: item.width,
              height: item.height,
            },
            general1: item.alt,
            general2: item.src,
          };
          applyFormItems.push(image);
          break;
        }
        case 'text': {
          // text の langCode 部分を抽出
          const reg = /^(text)\./;
          langCodes = [
            ...new Set(
              Object.keys(item)
                .filter((key) => reg.test(key))
                .map((key) => key.replace(reg, ''))
            ),
          ];
          const text: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '41',
            sort_no: sortNo,
            styles: {
              color: item.color,
              'text-align': item.alignment,
              'font-size': item.size,
              'font-weight': item['formats']?.includes('bold') ? 'bold' : undefined,
              'font-style': item['formats']?.includes('italic') ? 'italic' : undefined,
              'text-decoration': item['formats']?.includes('underlined') ? 'underline' : undefined,
            },
          };
          for (const langCode of langCodes) {
            text[`general1.${langCode}`] = item[`text.${langCode}`] ?? '';
          }
          applyFormItems.push(text);
          break;
        }
        case 'hr': {
          const hr: ApplyFormItem = {
            apply_form_no: item.apply_form_no!,
            format: '42',
            sort_no: sortNo,
            styles: {
              width: item.width,
              'border-color': item.color,
            },
          };
          applyFormItems.push(hr);
          break;
        }
      }
      sortNo++;
    });
    return applyFormItems;
  }

  /**
   * テンプレート/下書の応募フォーム情報をプレビューに変換
   * @param applyFormList
   * @returns
   */
  public static toPreviewItemList(
    applyFormList: Types.SetCommonSettingsAndFormItemsArgs['applyFormList'],
    opts?: ToItemListOptions
  ) {
    const isGlobal = false;
    const previewItems: Types.PreviewItem[] = [];
    const applyFormNos: number[] = applyFormList.map((applyForm) => applyForm.apply_form_no);
    const maxApplyFormNo: number = Math.max(...applyFormNos);
    applyFormList
      .toSorted(({ sort_no: a, apply_form_no: a2 }, { sort_no: b, apply_form_no: b2 }) =>
        a > 0 && b > 0 ? a - b : a2 - b2
      )
      .forEach(
        ({
          apply_form_no,
          lang = '0',
          sort_no,
          required,
          format,
          question,
          choices,
          value,
          style,
          place_holder,
          general1,
          general2,
          general3,
        }) => {
          const formatNum = Number(format);
          const styles = Utils.parseStyle(style);
          const requiredFlg = required !== Consts.REQUIRED.FALSE;
          let previewItem: Types.PreviewItem | null | undefined = null;
          // 各種テンプレート内2個目以降の要素の場合:最も後ろにある当該テンプレートを参照
          switch (formatNum) {
            case 51:
              previewItem = previewItems.findLast((p) => p?.element === 'template:artist');
              break;
            case 53:
            case 58:
              previewItem = previewItems.findLast((p) => p?.element === 'template:venue');
              break;
            case 56:
              previewItem = previewItems.findLast((p) => p?.element === 'parallel-input');
              break;
            case 11:
            case 12:
            case 13:
            case 14:
              previewItem = previewItems.findLast((p) => p?.element === 'template:address');
              if (!previewItem) {
                const index = sort_no > 0 ? sort_no : apply_form_no;
                previewItems[index] = previewItem = Utils.generateMinnimunPreviewItem(
                  'template:address',
                  maxApplyFormNo,
                  { currentApplyFormNo: apply_form_no, applyFormNos }
                );
              }
              break;
            default:
              const index = sort_no > 0 ? sort_no : apply_form_no;
              if (previewItems[index]) {
                previewItem = previewItems[index];
              } else {
                const previewItemType = ContentsHandler.convertFormat2PreviewItemType(format);
                previewItems[index] = previewItem = Utils.generateMinnimunPreviewItem(
                  previewItemType,
                  maxApplyFormNo,
                  { currentApplyFormNo: apply_form_no, applyFormNos }
                );
              }
              break;
          }
          if (!previewItem) return true;
          switch (formatNum) {
            case 1:
            case 2:
              (previewItem as Types.PreviewInput).type = 'text';
              (previewItem as Types.PreviewInput).lineBreaks = formatNum === 1 ? '0' : '1';
              (previewItem as Types.PreviewInput).required = requiredFlg;
              (previewItem as Types.PreviewInput)[`placeholder.${lang}`] = place_holder;
              (previewItem as Types.PreviewInput)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewInput)['label.color'] = styles.color;
              (previewItem as Types.PreviewInput).maxLength = general1;
              (previewItem as Types.PreviewInput).validation = general2 ?? Consts.VALIDATION.NONE;
              (previewItem as Types.PreviewInput).apply_form_no = apply_form_no;
              break;
            case 3:
            case 4:
              (previewItem as Types.PreviewCheck).selectable = general2;
              (previewItem as Types.PreviewCheck).required = requiredFlg;
              (previewItem as Types.PreviewCheck)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewCheck)['label.color'] = styles.color;
              (previewItem as Types.PreviewCheck).direction =
                general1 === Consts.DIRECTION.COL ? 'col' : 'row';
              (previewItem as Types.PreviewCheck).etcInput = general3 !== Consts.ETCINPUT.FALSE;
              const values = value?.split(',') ?? [];
              const labels = choices?.split(',') ?? [];
              const labelAndValues: Types.PreviewCheck['labelAndValues'] =
                (previewItem as Types.PreviewCheck).labelAndValues ??
                values.map((value) => ({ value }));
              (previewItem as Types.PreviewCheck).labelAndValues = labelAndValues.map(
                (labelAndValue, i) => ({
                  ...labelAndValue,
                  [`label.${lang}`]: labels[i] ?? '',
                })
              );
              (previewItem as Types.PreviewCheck).apply_form_no = apply_form_no;
              break;
            case 6:
              (previewItem as Types.PreviewSelectDate).required = requiredFlg;
              (previewItem as Types.PreviewSelectDate)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewSelectDate)['label.color'] = styles.color;
              (previewItem as Types.PreviewSelectDate).exclude = /^\d,\d,\d$/.test(general1 ?? '')
                ? parseInt(general1.replace(/,/g, ''), 2)
                : 0;
              const from = general2 ? Number(general2) : undefined;
              const to = general3 ? Number(general3) : undefined;
              (previewItem as Types.PreviewSelectDate)['year-range'] = {
                from: from == null || isNaN(from) ? undefined : from,
                to: to == null || isNaN(to) ? undefined : to,
              };
              (previewItem as Types.PreviewSelectDate).apply_form_no = apply_form_no;
              break;
            case 7:
              (previewItem as Types.PreviewInputLink).type = 'url';
              (previewItem as Types.PreviewInputLink).required = requiredFlg;
              (previewItem as Types.PreviewInputLink)[`placeholder.${lang}`] = place_holder;
              (previewItem as Types.PreviewInputLink)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewInputLink)['label.color'] = styles.color;
              (previewItem as Types.PreviewInputLink).maxLength = general1;
              (previewItem as Types.PreviewInputLink).apply_form_no = apply_form_no;
              break;
            case 8:
            case 9:
            case 17:
              (previewItem as Types.PreviewInputFile).type = 'file';
              (previewItem as Types.PreviewInputFile).fileType =
                formatNum === 8 ? 'image' : formatNum === 9 ? 'video' : 'audio';
              (previewItem as Types.PreviewInputFile).required = requiredFlg;
              (previewItem as Types.PreviewInputFile)[`placeholder.${lang}`] = place_holder;
              (previewItem as Types.PreviewInputFile)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewInputFile)['label.color'] = styles.color;
              (previewItem as Types.PreviewInputFile).apply_form_no = apply_form_no;
              break;
            case 11:
              // 住所:郵便番号
              (previewItem as Types.PreviewTemplateAddress)['postcode.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateAddress)[`postcode.label.text.${lang}`] =
                question;
              (previewItem as Types.PreviewTemplateAddress)['postcode.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateAddress)[`postcode.placeholder.${lang}`] =
                place_holder;
              (previewItem as Types.PreviewTemplateAddress)['postcode.exclude'] = false;
              (previewItem as Types.PreviewTemplateAddress)['postcode.apply_form_no'] =
                apply_form_no;
              (previewItem as Types.PreviewTemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 12:
              // 住所:都道府県
              (previewItem as Types.PreviewTemplateAddress)['prefecture.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateAddress)[`prefecture.label.text.${lang}`] =
                question;
              (previewItem as Types.PreviewTemplateAddress)['prefecture.label.color'] =
                styles.color;
              (previewItem as Types.PreviewTemplateAddress)['prefecture.exclude'] = false;
              (previewItem as Types.PreviewTemplateAddress)['prefecture.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateAddress)['prefecture.apply_form_no'] =
                apply_form_no;
              (previewItem as Types.PreviewTemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 13:
              // 住所:市区町村
              (previewItem as Types.PreviewTemplateAddress)['city.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateAddress)[`city.label.text.${lang}`] = question;
              (previewItem as Types.PreviewTemplateAddress)['city.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateAddress)[`city.placeholder.${lang}`] =
                place_holder;
              (previewItem as Types.PreviewTemplateAddress)['city.exclude'] = false;
              (previewItem as Types.PreviewTemplateAddress)['city.apply_form_no'] = apply_form_no;
              (previewItem as Types.PreviewTemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 14:
              // 住所:丁名番地 建物名・部屋番号
              (previewItem as Types.PreviewTemplateAddress)['others.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateAddress)[`others.label.text.${lang}`] = question;
              (previewItem as Types.PreviewTemplateAddress)['others.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateAddress)[`others.placeholder.${lang}`] =
                place_holder;
              (previewItem as Types.PreviewTemplateAddress)['others.exclude'] = false;
              (previewItem as Types.PreviewTemplateAddress)['others.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateAddress)['others.apply_form_no'] = apply_form_no;
              (previewItem as Types.PreviewTemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 16:
              (previewItem as Types.PreviewDropdownList).required = requiredFlg;
              (previewItem as Types.PreviewDropdownList)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewDropdownList)['label.color'] = styles.color;
              (previewItem as Types.PreviewDropdownList).etcInput =
                general3 !== Consts.ETCINPUT.FALSE;
              const dValues = value?.split(',') ?? [];
              const dLabels = choices?.split(',') ?? [];
              const dLabelAndValues: Types.PreviewDropdownList['labelAndValues'] =
                (previewItem as Types.PreviewDropdownList).labelAndValues ??
                dValues.map((value) => ({ value }));
              (previewItem as Types.PreviewDropdownList).labelAndValues = dLabelAndValues.map(
                (labelAndValue, i) => ({
                  ...labelAndValue,
                  [`label.${lang}`]: dLabels[i] ?? '',
                })
              );
              (previewItem as Types.PreviewDropdownList).apply_form_no = apply_form_no;
              break;
            case 20:
              // 同意書
              (previewItem as Types.PreviewTemplateConsent).required = requiredFlg;
              (previewItem as Types.PreviewTemplateConsent)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewTemplateConsent)['label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateConsent)[`placeholder.${lang}`] = place_holder;
              (previewItem as Types.PreviewTemplateConsent).apply_form_no = apply_form_no;
              break;
            case 40:
              (previewItem as Types.PreviewImage).src = general2;
              (previewItem as Types.PreviewImage).width = styles.width;
              (previewItem as Types.PreviewImage).height = styles.height;
              (previewItem as Types.PreviewImage).alt = general1;
              (previewItem as Types.PreviewImage).apply_form_no = apply_form_no;
              break;
            case 41:
              (previewItem as Types.PreviewText).color = styles.color;
              (previewItem as Types.PreviewText).size = styles['font-size'];
              (previewItem as Types.PreviewText).alignment = styles['text-align'];
              (previewItem as Types.PreviewText).formats = [
                styles['font-weight'] != null ? 'bold' : undefined,
                styles['font-style'] != null ? 'italic' : undefined,
                styles['text-decoration'] != null ? 'underlined' : undefined,
              ].join(',');
              (previewItem as Types.PreviewText)[`text.${lang}`] = general1;
              (previewItem as Types.PreviewText).apply_form_no = apply_form_no;
              break;
            case 42:
              (previewItem as Types.PreviewHorizontalRule).width = styles.width;
              (previewItem as Types.PreviewHorizontalRule).color = styles['border-color'];
              (previewItem as Types.PreviewHorizontalRule).apply_form_no = apply_form_no;
              break;
            case 50:
              // アーティスト名
              (previewItem as Types.PreviewTemplateArtist)['showAutoFill'] = general1
                ? general1 === Consts.SHOW.TRUE
                  ? true
                  : false
                : true;
              (previewItem as Types.PreviewTemplateArtist)[`name.label.text.${lang}`] = question;
              (previewItem as Types.PreviewTemplateArtist)['name.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateArtist)['name.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateArtist)[`name.placeholder.${lang}`] =
                place_holder ?? '';
              (previewItem as Types.PreviewTemplateArtist)['name.apply_form_no'] = apply_form_no;
              break;
            case 51:
              // アーティスト名カナ
              (previewItem as Types.PreviewTemplateArtist)[`kana.label.text.${lang}`] = question;
              (previewItem as Types.PreviewTemplateArtist)['kana.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateArtist)['kana.required'] = requiredFlg;
              (previewItem as Types.PreviewTemplateArtist)[`kana.placeholder.${lang}`] =
                place_holder ?? '';
              (previewItem as Types.PreviewTemplateArtist)['kana.apply_form_no'] = apply_form_no;
              break;
            case 52:
              // 会場:入力
              (previewItem as Types.PreviewTemplateVenue).required = requiredFlg;
              (previewItem as Types.PreviewTemplateVenue)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewTemplateVenue)['label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateVenue)['examination_no'] = parseInt(
                general1,
                10
              );
              (previewItem as Types.PreviewTemplateVenue)['displayNameType'] =
                general2 === Consts.VENUE_DISPLAY_TYPE.VENUE ? '1' : '0';
              (previewItem as Types.PreviewTemplateVenue)['venue.apply_form_no'] = apply_form_no;
              (previewItem as Types.PreviewTemplateVenue)[`labelAndValues`] =
                opts?.['template:venue']?.preferred_venue_list ?? [];
              break;
            case 53:
              // 会場:注意書き
              const size = Number(styles['font-size']);
              (previewItem as Types.PreviewTemplateVenue)['note.color'] = styles.color;
              (previewItem as Types.PreviewTemplateVenue)['note.size'] = isNaN(size) ? 1 : size;
              (previewItem as Types.PreviewTemplateVenue)[`note.text.${lang}`] = general1;
              (previewItem as Types.PreviewTemplateVenue)['note.apply_form_no'] = apply_form_no;
              (previewItem as Types.PreviewTemplateVenue)['note.exclude'] = false;
              break;
            case 58:
              // 会場:参加可能会場
              (previewItem as Types.PreviewTemplateVenue)['displayNameType'] =
                general2 === Consts.VENUE_DISPLAY_TYPE.VENUE ? '1' : '0';
              (previewItem as Types.PreviewTemplateVenue)[`possibility.text.${lang}`] = question;
              (previewItem as Types.PreviewTemplateVenue)['possibility.color'] = styles.color;
              (previewItem as Types.PreviewTemplateVenue)['possibility.apply_form_no'] =
                apply_form_no;
              (previewItem as Types.PreviewTemplateVenue)['possibility.exclude'] = false;
              break;
            case 54:
              // メンバー情報
              const [
                input1Exclude = '0',
                input2Exclude = '0',
                input3Exclude = '0',
                input4Exclude = '0',
                input5Exclude = '0',
                input6Exclude = '0',
              ] = general1.split(',');
              const [
                input1LabelText = lang === '0' ? 'メンバー名' : 'name',
                input2LabelText = lang === '0' ? 'ヨミガナ' : '',
                input3LabelText = lang === '0' ? '年齢' : 'age',
                input4LabelText = lang === '0' ? '性別' : 'gender',
                input5LabelText = lang === '0' ? 'パート' : 'part',
                input6LabelText = lang === '0'
                  ? 'メンバー/サポートメンバー'
                  : 'Members/Support Members',
              ] = general3.split(',');
              (previewItem as Types.PreviewTemplateMembers)['input1.exclude'] =
                input1Exclude === '0';
              (previewItem as Types.PreviewTemplateMembers)['input2.exclude'] =
                input2Exclude === '0';
              (previewItem as Types.PreviewTemplateMembers)['input3.exclude'] =
                input3Exclude === '0';
              (previewItem as Types.PreviewTemplateMembers)['input4.exclude'] =
                input4Exclude === '0';
              (previewItem as Types.PreviewTemplateMembers)['input5.exclude'] =
                input5Exclude === '0';
              (previewItem as Types.PreviewTemplateMembers)['input6.exclude'] =
                input6Exclude === '0';
              (previewItem as Types.PreviewTemplateMembers)[`input1.label.text.${lang}`] =
                input1LabelText;
              (previewItem as Types.PreviewTemplateMembers)[`input2.label.text.${lang}`] =
                input2LabelText;
              (previewItem as Types.PreviewTemplateMembers)[`input3.label.text.${lang}`] =
                input3LabelText;
              (previewItem as Types.PreviewTemplateMembers)[`input4.label.text.${lang}`] =
                input4LabelText;
              (previewItem as Types.PreviewTemplateMembers)[`input5.label.text.${lang}`] =
                input5LabelText;
              (previewItem as Types.PreviewTemplateMembers)[`input6.label.text.${lang}`] =
                input6LabelText;
              (previewItem as Types.PreviewTemplateMembers)['input1.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateMembers)['input2.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateMembers)['input3.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateMembers)['input4.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateMembers)['input5.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateMembers)['input6.label.color'] = styles.color;
              (previewItem as Types.PreviewTemplateMembers)[`consent.label.text.${lang}`] =
                general2;
              (previewItem as Types.PreviewTemplateMembers)['isGlobal'] = isGlobal;
              const [
                input1Placeholder = '',
                input2Placeholder = '',
                input3Placeholder = '',
                input5Placeholder = '',
                _,
              ] = place_holder.split(',');
              (previewItem as Types.PreviewTemplateMembers)[`input1.placeholder.${lang}`] =
                input1Placeholder;
              (previewItem as Types.PreviewTemplateMembers)[`input2.placeholder.${lang}`] =
                input2Placeholder;
              (previewItem as Types.PreviewTemplateMembers)[`input3.placeholder.${lang}`] =
                input3Placeholder;
              (previewItem as Types.PreviewTemplateMembers)[`input5.placeholder.${lang}`] =
                input5Placeholder;
              (previewItem as Types.PreviewTemplateMembers).apply_form_no = apply_form_no;
              break;
            case 55:
              // 横並び入力欄:左
              (previewItem as Types.PreviewParallelInput)[`left.label.text.${lang}`] = question;
              (previewItem as Types.PreviewParallelInput)['left.label.color'] = styles.color;
              (previewItem as Types.PreviewParallelInput)['left.required'] = requiredFlg;
              (previewItem as Types.PreviewParallelInput)[`left.placeholder.${lang}`] =
                place_holder ?? '';
              (previewItem as Types.PreviewParallelInput)['left.apply_form_no'] = apply_form_no;
              break;
            case 56:
              // 横並び入力欄:右
              (previewItem as Types.PreviewParallelInput)[`right.label.text.${lang}`] = question;
              (previewItem as Types.PreviewParallelInput)['right.label.color'] = styles.color;
              (previewItem as Types.PreviewParallelInput)['right.required'] = requiredFlg;
              (previewItem as Types.PreviewParallelInput)[`right.placeholder.${lang}`] =
                place_holder ?? '';
              (previewItem as Types.PreviewParallelInput)['right.apply_form_no'] = apply_form_no;
              break;
            case 57:
              (previewItem as Types.PreviewCategory).selectable = general2;
              (previewItem as Types.PreviewCategory).required = requiredFlg;
              (previewItem as Types.PreviewCategory)[`label.text.${lang}`] = question;
              (previewItem as Types.PreviewCategory)['label.color'] = styles.color;
              (previewItem as Types.PreviewCategory).direction =
                general1 === Consts.DIRECTION.COL ? 'col' : 'row';
              const categoryValues = value?.split(',') ?? [];
              const categoryLabels = choices?.split(',') ?? [];
              const categoryLabelAndValues: Types.PreviewCategory['labelAndValues'] =
                (previewItem as Types.PreviewCategory).labelAndValues ??
                categoryValues.map((value) => ({ value }));
              (previewItem as Types.PreviewCategory).labelAndValues = categoryLabelAndValues.map(
                (labelAndValue, i) => ({
                  ...labelAndValue,
                  [`label.${lang}`]: categoryLabels[i] ?? '',
                })
              );
              (previewItem as Types.PreviewCategory).apply_form_no = apply_form_no;
              break;
          }
        }
      );
    return [...previewItems].filter((el) => !!el);
  }

  /**
   * テンプレートの応募フォーム情報をフォーム要素に変換
   * @param applyFormList
   * @returns
   */
  public static toItemList(
    applyFormList: Types.SetCommonSettingsAndFormItemsArgs['applyFormList'],
    opts?: ToItemListOptions
  ) {
    const isGlobal = opts?.isGlobal ?? false;
    const items: Types.Item[] = [];
    applyFormList
      .toSorted(({ sort_no: a, apply_form_no: a2 }, { sort_no: b, apply_form_no: b2 }) =>
        a > 0 && b > 0 ? a - b : a2 - b2
      )
      .forEach(
        ({
          apply_form_no,
          lang = '0',
          sort_no,
          required,
          format,
          question,
          choices,
          value,
          style,
          place_holder,
          general1,
          general2,
          general3,
        }) => {
          const formatNum = Number(format);
          const styles = Utils.parseStyle(style);
          const requiredFlg = required !== Consts.REQUIRED.FALSE;
          let item: Types.Item | null | undefined = null;
          // 各種テンプレート内2個目以降の要素の場合:最も後ろにある当該テンプレートを参照
          switch (formatNum) {
            case 51:
              item = items.findLast((p) => p?.element === 'template:artist');
              break;
            case 53:
            case 58:
              item = items.findLast((p) => p?.element === 'template:venue');
              break;
            case 56:
              item = items.findLast((p) => p?.element === 'parallel-input');
              break;
            case 11:
            case 12:
            case 13:
            case 14:
              item = items.findLast((p) => p?.element === 'template:address');
              if (!item) {
                const index = sort_no > 0 ? sort_no : apply_form_no;
                items[index] = item = Utils.generateDefaultItem('template:address');
              }
              break;
            default:
              const index = sort_no > 0 ? sort_no : apply_form_no;
              if (items[index]) {
                item = items[index];
              } else {
                const itemType = ContentsHandler.convertFormat2ItemType(format);
                items[index] = item = Utils.generateDefaultItem(itemType);
              }
              break;
          }
          if (!item) return true;
          switch (formatNum) {
            case 1:
            case 2:
              (item as Types.Input).type = 'text';
              (item as Types.Input).lineBreaks = formatNum === 1 ? '0' : '1';
              (item as Types.Input).required = requiredFlg;
              (item as Types.Input)[`placeholder.${lang}`] = place_holder;
              (item as Types.Input)[`label.text.${lang}`] = question;
              (item as Types.Input)['label.color'] = styles.color;
              (item as Types.Input).maxLength = general1;
              (item as Types.Input).validation = general2 ?? Consts.VALIDATION.NONE;
              (item as Types.Input).apply_form_no = apply_form_no;
              break;
            case 3:
            case 4:
              (item as Types.Check).selectable = general2;
              (item as Types.Check).required = requiredFlg;
              (item as Types.Check)[`label.text.${lang}`] = question;
              (item as Types.Check)['label.color'] = styles.color;
              (item as Types.Check).direction = general1 === Consts.DIRECTION.COL ? 'col' : 'row';
              (item as Types.Check).etcInput = general3 !== Consts.ETCINPUT.FALSE;
              const values = value?.split(',') ?? [];
              const labels = choices?.split(',') ?? [];
              const labelAndValues: Types.Check['labelAndValues'] =
                (item as Types.Check).labelAndValues ?? values.map((value) => ({ value }));

              (item as Types.Check).labelAndValues = labelAndValues
                .map((labelAndValue, i) => ({
                  ...labelAndValue,
                  [`label.${lang}` as const]: labels[i] ?? '',
                }))
                .map((labelAndValue) =>
                  // コントロールされていないInput要素で、最終的にラベルと値を保存したいため、JSON.stringifyで保存
                  ({
                    ...labelAndValue,
                    value: JSON.stringify({
                      value: labelAndValue.value,
                      //@ts-expect-error
                      label: labelAndValue[`label.${lang}` as const] ?? '',
                    }),
                  })
                );
              (item as Types.Check).apply_form_no = apply_form_no;
              break;
            case 6:
              (item as Types.SelectDate).required = requiredFlg;
              (item as Types.SelectDate)[`label.text.${lang}`] = question;
              (item as Types.SelectDate)['label.color'] = styles.color;
              (item as Types.SelectDate).exclude = /^\d,\d,\d$/.test(general1 ?? '')
                ? parseInt(general1.replace(/,/g, ''), 2)
                : 0;
              const from = general2 ? Number(general2) : undefined;
              const to = general3 ? Number(general3) : undefined;
              (item as Types.SelectDate)['year-range'] = {
                from: from == null || isNaN(from) ? undefined : from,
                to: to == null || isNaN(to) ? undefined : to,
              };
              (item as Types.SelectDate).apply_form_no = apply_form_no;
              break;
            case 7:
              (item as Types.InputLink).type = 'url';
              (item as Types.InputLink).required = requiredFlg;
              (item as Types.InputLink)[`placeholder.${lang}`] = place_holder;
              (item as Types.InputLink)[`label.text.${lang}`] = question;
              (item as Types.InputLink)['label.color'] = styles.color;
              (item as Types.InputLink).maxLength = general1;
              (item as Types.InputLink).apply_form_no = apply_form_no;
              break;
            case 8:
            case 9:
            case 17:
              (item as Types.InputFile).type = 'file';
              (item as Types.InputFile).fileType =
                formatNum === 8 ? 'image' : formatNum === 9 ? 'video' : 'audio';
              (item as Types.InputFile).required = requiredFlg;
              (item as Types.InputFile)[`placeholder.${lang}`] = place_holder;
              (item as Types.InputFile)[`label.text.${lang}`] = question;
              (item as Types.InputFile)['label.color'] = styles.color;
              (item as Types.InputFile).apply_form_no = apply_form_no;
              break;
            case 11:
              // 住所:郵便番号
              (item as Types.TemplateAddress)['postcode.required'] = requiredFlg;
              (item as Types.TemplateAddress)[`postcode.label.text.${lang}`] = question;
              (item as Types.TemplateAddress)['postcode.label.color'] = styles.color;
              (item as Types.TemplateAddress)[`postcode.placeholder.${lang}`] = place_holder;
              (item as Types.TemplateAddress)['postcode.exclude'] = false;
              (item as Types.TemplateAddress)['postcode.apply_form_no'] = apply_form_no;
              (item as Types.TemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 12:
              // 住所:都道府県
              (item as Types.TemplateAddress)['prefecture.required'] = requiredFlg;
              (item as Types.TemplateAddress)[`prefecture.label.text.${lang}`] = question;
              (item as Types.TemplateAddress)['prefecture.label.color'] = styles.color;
              (item as Types.TemplateAddress)['prefecture.exclude'] = false;
              (item as Types.TemplateAddress)['prefecture.required'] = requiredFlg;
              (item as Types.TemplateAddress)['prefecture.apply_form_no'] = apply_form_no;
              (item as Types.TemplateAddress)['prefecture.labelAndValues'] =
                opts?.['template:address']?.prefectures ?? [];
              (item as Types.TemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 13:
              // 住所:市区町村
              (item as Types.TemplateAddress)['city.required'] = requiredFlg;
              (item as Types.TemplateAddress)[`city.label.text.${lang}`] = question;
              (item as Types.TemplateAddress)['city.label.color'] = styles.color;
              (item as Types.TemplateAddress)[`city.placeholder.${lang}`] = place_holder;
              (item as Types.TemplateAddress)['city.exclude'] = false;
              (item as Types.TemplateAddress)['city.apply_form_no'] = apply_form_no;
              (item as Types.TemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 14:
              // 住所:丁名番地 建物名・部屋番号
              (item as Types.TemplateAddress)['others.required'] = requiredFlg;
              (item as Types.TemplateAddress)[`others.label.text.${lang}`] = question;
              (item as Types.TemplateAddress)['others.label.color'] = styles.color;
              (item as Types.TemplateAddress)[`others.placeholder.${lang}`] = place_holder;
              (item as Types.TemplateAddress)['others.exclude'] = false;
              (item as Types.TemplateAddress)['others.required'] = requiredFlg;
              (item as Types.TemplateAddress)['others.apply_form_no'] = apply_form_no;
              (item as Types.TemplateAddress)['isGlobal'] = isGlobal;
              break;
            case 16:
              (item as Types.DropdownList).required = requiredFlg;
              (item as Types.DropdownList)[`label.text.${lang}`] = question;
              (item as Types.DropdownList)['label.color'] = styles.color;
              (item as Types.DropdownList).etcInput = general3 !== Consts.ETCINPUT.FALSE;
              const dValues = value?.split(',') ?? [];
              const dLabels = choices?.split(',') ?? [];
              const dLabelAndValues: Types.DropdownList['labelAndValues'] =
                (item as Types.DropdownList).labelAndValues ?? dValues.map((value) => ({ value }));

              (item as Types.DropdownList).labelAndValues = dLabelAndValues
                .map((labelAndValue, i) => ({
                  ...labelAndValue,
                  [`label.${lang}` as const]: dLabels[i] ?? '',
                }))
                .map((labelAndValue) =>
                  // コントロールされていないInput要素で、最終的にラベルと値を保存したいため、JSON.stringifyで保存
                  ({
                    ...labelAndValue,
                    value: JSON.stringify({
                      value: labelAndValue.value,
                      //@ts-expect-error
                      label: labelAndValue[`label.${lang}` as const] ?? '',
                    }),
                  })
                );
              (item as Types.DropdownList).apply_form_no = apply_form_no;
              break;
            case 20:
              // 同意書
              (item as Types.TemplateConsent).required = requiredFlg;
              (item as Types.TemplateConsent)[`label.text.${lang}`] = question;
              (item as Types.TemplateConsent)['label.color'] = styles.color;
              (item as Types.TemplateConsent)[`placeholder.${lang}`] = place_holder;
              (item as Types.TemplateConsent).apply_form_no = apply_form_no;
              break;
            case 40:
              (item as Types.Image).src = opts?.image?.getSrc
                ? opts.image.getSrc(general2)
                : general2;
              (item as Types.Image).width = styles.width;
              (item as Types.Image).height = styles.height;
              (item as Types.Image).alt = general1;
              break;
            case 41:
              (item as Types.Text).color = styles.color;
              (item as Types.Text).size = styles['font-size'];
              (item as Types.Text).alignment = styles['text-align'];
              (item as Types.Text).formats = [
                styles['font-weight'] != null ? 'bold' : undefined,
                styles['font-style'] != null ? 'italic' : undefined,
                styles['text-decoration'] != null ? 'underlined' : undefined,
              ].join(',');
              (item as Types.Text)[`text.${lang}`] = general1;
              break;
            case 42:
              (item as Types.HorizontalRule).width = styles.width;
              (item as Types.HorizontalRule).color = styles['border-color'];
              break;
            case 50:
              // アーティスト名
              (item as Types.TemplateArtist)['showAutoFill'] = general1
                ? general1 === Consts.SHOW.TRUE
                  ? true
                  : false
                : true;
              (item as Types.TemplateArtist)[`name.label.text.${lang}`] = question;
              (item as Types.TemplateArtist)['name.label.color'] = styles.color;
              (item as Types.TemplateArtist)['name.required'] = requiredFlg;
              (item as Types.TemplateArtist)[`name.placeholder.${lang}`] = place_holder ?? '';
              (item as Types.TemplateArtist)['name.apply_form_no'] = apply_form_no;
              break;
            case 51:
              // アーティスト名カナ
              (item as Types.TemplateArtist)[`kana.label.text.${lang}`] = question;
              (item as Types.TemplateArtist)['kana.label.color'] = styles.color;
              (item as Types.TemplateArtist)['kana.required'] = requiredFlg;
              (item as Types.TemplateArtist)[`kana.placeholder.${lang}`] = place_holder ?? '';
              (item as Types.TemplateArtist)['kana.apply_form_no'] = apply_form_no;
              break;
            case 52:
              // 会場:入力
              (item as Types.TemplateVenue).required = requiredFlg;
              (item as Types.TemplateVenue)[`label.text.${lang}`] = question;
              (item as Types.TemplateVenue)['label.color'] = styles.color;
              (item as Types.TemplateVenue)['examination_no'] = parseInt(general1, 10);
              (item as Types.TemplateVenue)['venue.apply_form_no'] = apply_form_no;
              (item as Types.TemplateVenue)['displayNameType'] =
                general2 === Consts.VENUE_DISPLAY_TYPE.VENUE ? '1' : '0';
              (item as Types.TemplateVenue)['labelAndValues'] =
                opts?.['template:venue']?.preferred_venue_list ?? [];
              break;
            case 53:
              // 会場:注意書き
              const size = Number(styles['font-size']);
              (item as Types.TemplateVenue)['note.color'] = styles.color;
              (item as Types.TemplateVenue)['note.size'] = isNaN(size) ? 1 : size;
              (item as Types.TemplateVenue)[`note.text.${lang}`] = general1;
              (item as Types.TemplateVenue)['note.apply_form_no'] = apply_form_no;
              (item as Types.TemplateVenue)['note.exclude'] = false;
              break;
            case 58:
              // 会場:参加可能会場
              (item as Types.TemplateVenue)[`possibility.text.${lang}`] = question;
              (item as Types.TemplateVenue)['possibility.color'] = styles.color;
              (item as Types.TemplateVenue)['displayNameType'] =
                general2 === Consts.VENUE_DISPLAY_TYPE.VENUE ? '1' : '0';
              (item as Types.TemplateVenue)['possibility.apply_form_no'] = apply_form_no;
              (item as Types.TemplateVenue)['possibility.exclude'] = false;
              break;
            case 54:
              // メンバー情報
              const [
                nameExclude = '0',
                yomiganaExclude = '0',
                ageExclude = '0',
                genderExclude = '0',
                partExclude = '0',
                memberTypeExclude = '0',
              ] = general1.split(',');
              const [
                input1LabelText = lang === '0' ? 'メンバー名' : 'name',
                input2LabelText = lang === '0' ? 'ヨミガナ' : '',
                input3LabelText = lang === '0' ? '年齢' : 'age',
                input4LabelText = lang === '0' ? '性別' : 'gender',
                input5LabelText = lang === '0' ? 'パート' : 'part',
                input6LabelText = lang === '0'
                  ? 'メンバー/サポートメンバー'
                  : 'Members/Support Members',
              ] = general3.split(',');
              (item as Types.TemplateMembers)['input1.exclude'] = nameExclude === '0';
              (item as Types.TemplateMembers)['input2.exclude'] = yomiganaExclude === '0';
              (item as Types.TemplateMembers)['input3.exclude'] = ageExclude === '0';
              (item as Types.TemplateMembers)['input4.exclude'] = genderExclude === '0';
              (item as Types.TemplateMembers)['input5.exclude'] = partExclude === '0';
              (item as Types.TemplateMembers)['input6.exclude'] = memberTypeExclude === '0';
              (item as Types.TemplateMembers)[`input1.label.text.${lang}`] = input1LabelText;
              (item as Types.TemplateMembers)[`input2.label.text.${lang}`] = input2LabelText;
              (item as Types.TemplateMembers)[`input3.label.text.${lang}`] = input3LabelText;
              (item as Types.TemplateMembers)[`input4.label.text.${lang}`] = input4LabelText;
              (item as Types.TemplateMembers)[`input5.label.text.${lang}`] = input5LabelText;
              (item as Types.TemplateMembers)[`input6.label.text.${lang}`] = input6LabelText;
              (item as Types.TemplateMembers)['input1.label.color'] = styles.color;
              (item as Types.TemplateMembers)['input2.label.color'] = styles.color;
              (item as Types.TemplateMembers)['input3.label.color'] = styles.color;
              (item as Types.TemplateMembers)['input4.label.color'] = styles.color;
              (item as Types.TemplateMembers)['input5.label.color'] = styles.color;
              (item as Types.TemplateMembers)['input6.label.color'] = styles.color;
              (item as Types.TemplateMembers)[`consent.label.text.${lang}`] = general2;
              (item as Types.TemplateMembers)['isGlobal'] = isGlobal;
              const [
                input1Placeholder = '',
                input2Placeholder = '',
                input3Placeholder = '',
                input5Placeholder = '',
                _,
              ] = place_holder.split(',');
              (item as Types.TemplateMembers)[`input1.placeholder.${lang}`] = input1Placeholder;
              (item as Types.TemplateMembers)[`input2.placeholder.${lang}`] = input2Placeholder;
              (item as Types.TemplateMembers)[`input3.placeholder.${lang}`] = input3Placeholder;
              (item as Types.TemplateMembers)[`input5.placeholder.${lang}`] = input5Placeholder;
              (item as Types.TemplateMembers).apply_form_no = apply_form_no;
              break;
            case 55:
              // 横並び入力欄:左
              (item as Types.ParallelInput)[`left.label.text.${lang}`] = question;
              (item as Types.ParallelInput)['left.label.color'] = styles.color;
              (item as Types.ParallelInput)['left.required'] = requiredFlg;
              (item as Types.ParallelInput)[`left.placeholder.${lang}`] = place_holder ?? '';
              (item as Types.ParallelInput)['left.apply_form_no'] = apply_form_no;
              break;
            case 56:
              // 横並び入力欄:右
              (item as Types.ParallelInput)[`right.label.text.${lang}`] = question;
              (item as Types.ParallelInput)['right.label.color'] = styles.color;
              (item as Types.ParallelInput)['right.required'] = requiredFlg;
              (item as Types.ParallelInput)[`right.placeholder.${lang}`] = place_holder ?? '';
              (item as Types.ParallelInput)['right.apply_form_no'] = apply_form_no;
              break;
            case 57:
              (item as Types.Category).selectable = general2;
              (item as Types.Category).required = requiredFlg;
              (item as Types.Category)[`label.text.${lang}`] = question;
              (item as Types.Category)['label.color'] = styles.color;
              (item as Types.Category).direction =
                general1 === Consts.DIRECTION.COL ? 'col' : 'row';
              const categoryValues = value?.split(',') ?? [];
              const categoryLabels = choices?.split(',') ?? [];
              const categoryLabelAndValues: Types.Category['labelAndValues'] =
                (item as Types.Category).labelAndValues ??
                categoryValues.map((value) => ({ value }));

              (item as Types.Category).labelAndValues = categoryLabelAndValues
                .map((labelAndValue, i) => ({
                  ...labelAndValue,
                  [`label.${lang}` as const]: categoryLabels[i] ?? '',
                }))
                .map((labelAndValue) =>
                  // コントロールされていないInput要素で、最終的にラベルと値を保存したいため、JSON.stringifyで保存
                  ({
                    ...labelAndValue,
                    value: JSON.stringify({
                      value: labelAndValue.value,
                      //@ts-expect-error
                      label: labelAndValue[`label.${lang}` as const] ?? '',
                    }),
                  })
                );
              (item as Types.Category).apply_form_no = apply_form_no;
              break;
          }
        }
      );
    return [...items].filter((el) => !!el);
  }

  /**
   * `(テンプレート/下書).format`をプレビュー種類に変換
   * @param format
   * @returns
   */
  private static convertFormat2PreviewItemType(
    format: Types.SetCommonSettingsAndFormItemsArgs['applyFormList'][number]['format']
  ): Types.PreviewItemType {
    switch (Number(format)) {
      case 1:
      case 2:
        return 'input';
      case 7:
        return 'input:url';
      case 8:
      case 9:
      case 17:
        return 'input:image';
      case 3:
      case 4:
        return 'check';
      case 6:
        return 'select-date';
      case 11:
      case 12:
      case 13:
      case 14:
        return 'template:address';
      case 16:
        return 'dropdown-list';
      case 20:
        return 'template:consent';
      case 40:
        return 'image';
      case 41:
        return 'text';
      case 42:
        return 'hr';
      case 50:
      case 51:
        return 'template:artist';
      case 52:
      case 53:
      case 58:
        return 'template:venue';
      case 54:
        return 'template:members';
      case 55:
      case 56:
        return 'parallel-input';
      case 57:
        return 'category';
      default:
        return 'gohst';
    }
  }

  /**
   * `(テンプレート).format`を種類に変換
   * @param format
   * @returns
   */
  private static convertFormat2ItemType(
    format: Types.SetCommonSettingsAndFormItemsArgs['applyFormList'][number]['format']
  ): Types.ItemType {
    switch (Number(format)) {
      case 1:
      case 2:
        return 'input';
      case 7:
        return 'input:url';
      case 8:
      case 9:
      case 17:
        return 'input:image';
      case 3:
      case 4:
        return 'check';
      case 6:
        return 'select-date';
      case 11:
      case 12:
      case 13:
      case 14:
        return 'template:address';
      case 16:
        return 'dropdown-list';
      case 20:
        return 'template:consent';
      case 40:
        return 'image';
      case 41:
        return 'text';
      case 42:
        return 'hr';
      case 50:
      case 51:
        return 'template:artist';
      case 52:
      case 53:
      case 58:
        return 'template:venue';
      case 54:
        return 'template:members';
      case 55:
      case 56:
        return 'parallel-input';
      case 57:
        return 'category';
      default:
        throw new Error('不正なフォーマットです');
    }
  }
}

/** コンポーネント群 */
export namespace Components {
  type FormUnitProps = HTMLAttributes<HTMLDivElement>;
  export function FormUnit({ className: additionalClassName = '', ...props }: FormUnitProps) {
    const className = `grow shrink max-540:mt-[10px] ${additionalClassName}`;
    return <div className={className} {...props} />;
  }

  type RequiredLabelProps = React.HTMLAttributes<HTMLSpanElement>;
  export const RequiredLabel = forwardRef<HTMLSpanElement, RequiredLabelProps>(
    ({ ...props }, ref) => {
      return (
        <ContentsHandlerContext.Consumer>
          {({ t }) => (
            <span
              className="bg-[#d42323] font-bold text-[#fff] px-2 rounded-full border border-white"
              {...props}
              ref={ref}
            >
              {t('CH_012')}
            </span>
          )}
        </ContentsHandlerContext.Consumer>
      );
    }
  );

  export type FormLabelProps = LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean };
  export const FormLabel = forwardRef<HTMLLabelElement, FormLabelProps>(function (
    { className: additionalClassName = '', children, required, ...props },
    forwardedRef
  ) {
    const baseClassName =
      'flex mb-[0.5em] text-[0.875em] font-bold text-[color:#028] max-1080:text-[0.75em]';
    const className = `${baseClassName} ${additionalClassName}`;
    return (
      <ContentsHandlerComponentsContext.Consumer>
        {({ RequiredLabel }) => (
          <label className={className} {...props} ref={forwardedRef}>
            {children}
            {required && <RequiredLabel />}
          </label>
        )}
      </ContentsHandlerComponentsContext.Consumer>
    );
  });

  type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
  export const Input = forwardRef<HTMLInputElement, InputProps>(function (
    { className: additionalClassName = '', ...rest },
    ref
  ) {
    const baseClassName =
      'p-[0.625em] text-[color:#555] bg-[color:#fff] border-solid border-b-2 border-[color:#ddd] rounded-[4px] [inline-size:100%]';
    const readOnlyClassName =
      'read-only:text-[color:#555] read-only:bg-[color:rgb(255 255 255 / 90%)] read-only:border-t-0 read-only:border-r-0 read-only:border-l-0 read-only:border-b-[1px] read-only:rounded-none read-only:outline-none';
    const className = `${baseClassName} ${readOnlyClassName} ${additionalClassName}`;
    return <input {...rest} className={className} ref={ref} />;
  });

  type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;
  export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
    { className: additionalClassName = '', ...props },
    forwardedRef
  ) {
    const baseClassName =
      'max-w-full py-[0.625em] pl-[0.625em] pr-[1.25em] text-[color:#555] border-solid border-2 border-[color:#ddd] rounded-[4px] bg-[color:#fff] bg-select-icon bg-no-repeat [background-position:right_-0.5em_center] bg-[length:2em]';
    const className = `${baseClassName} ${additionalClassName}`;
    return <select className={className} {...props} ref={forwardedRef} />;
  });

  type FileInputProps = {
    label?: string;
    fileObjectUrls?: string[];
    onChangeFiles?: (files: File[]) => unknown;
  } & InputHTMLAttributes<HTMLInputElement>;
  export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(function FileInput(
    {
      label = 'ファイルを選択',
      onChangeFiles,
      value,
      fileObjectUrls = [],
      className: additionalClassName = '',
      onChange: _onChange,
      ...rest
    },
    forwardedRef
  ) {
    const [isFileDraggedOver, setIsFileDraggedOver] = useState(false);
    const [files, setFiles] = useState<File[]>([]);

    // ファイル変更時
    useEffect(() => {
      onChangeFiles && onChangeFiles(files);
    }, [files, onChangeFiles]);

    /**
     * input[type=file]のファイル変更時
     *
     * input[type=file]のvalueをjs側から設定することはできないので
     *
     * 選択fileはstateで管理
     */
    const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        e.preventDefault();
        const { files } = e.currentTarget;
        if (files && files.length > 0) {
          setFiles(Array.from(files));
        }
        _onChange && _onChange(e);
      },
      [_onChange]
    );

    // dnd関連
    const onDragOver = useCallback<DragEventHandler<HTMLElement>>((e) => {
      const { types, files } = e.dataTransfer;
      if (types.length > 0 && types[0] === 'Files' && files.length > 0) {
        setIsFileDraggedOver(true);
      }
    }, []);
    const onDragLeave = useCallback<DragEventHandler<HTMLElement>>((e) => {
      setIsFileDraggedOver(false);
    }, []);
    const onDrop = useCallback<DragEventHandler<HTMLElement>>((e) => {
      const { types, files } = e.dataTransfer;
      if (types.length > 0 && types[0] === 'Files' && files.length > 0) {
        setFiles(Array.from(files));
      }
      setIsFileDraggedOver(false);
    }, []);

    const dispValue = (() => {
      const disp = value ?? files.map((file) => file.name).join(', ');
      if (disp === '' || (Array.isArray(disp) && disp.length === 0)) {
        return label;
      } else {
        return disp;
      }
    })();
    const inputLikeClassName =
      'p-[0.625em] text-[color:#555] bg-[color:#fff] border-solid border-b-2 border-[color:#ddd] rounded-[4px] [inline-size:100%]';
    const labelBaseClassName = 'cursor-pointer inline-block w-full';
    const className = `${inputLikeClassName} ${labelBaseClassName} ${additionalClassName}`;
    return (
      <label
        className={className}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
      >
        {isFileDraggedOver ? 'ファイルをドロップ' : dispValue}
        <input
          {...rest}
          type="file"
          onChange={onChange}
          ref={forwardedRef}
          className="opacity-0 w-1"
        />
      </label>
    );
  });

  type RadioProps = { children?: ReactNode } & InputHTMLAttributes<HTMLInputElement>;
  export const Radio = forwardRef<HTMLInputElement, RadioProps>(function (
    { children, className = '', type: _, ...props },
    forwardedRef
  ) {
    const hiddenClassName = 'opacity-0 absolute w-full h-full top-0 left-0';
    const labelBaseClassName = 'relative flex gap-x-[0.125em] items-center';
    const labelBeforeClassName =
      "before:w-[20px] before:h-[20px] before:border-solid before:border-2 before:border-[color:#bbb] transition-all duration-[0.4s] before:bg-white before:rounded-[50%] before:content-['']";
    const labelAfterClassName =
      "after:absolute after:transition-all after:duration-[0.4s] after:top-1/2 after:left-[6px] after:w-[8px] after:h-[8px] after:[transform:translateY(-50%)] after:content-[''] after:bg-transparent after:rounded-[50%]";
    const checkedBeforeClassName =
      '[&:checked_~_span::before]:border-[color:#cf2a69] [&:checked_~_span::before]:bg-[color:#cf2a69]';
    const checkedAfterClassName = '[&:checked_~_span::after]:bg-white';
    return (
      <label className={`relative ${className}`}>
        <input
          type="radio"
          className={`${hiddenClassName} ${checkedBeforeClassName} ${checkedAfterClassName}`}
          {...props}
          ref={forwardedRef}
        />
        <span className={`${labelBaseClassName} ${labelBeforeClassName} ${labelAfterClassName}`}>
          {children}
        </span>
      </label>
    );
  });

  type CheckboxProps = { children?: ReactNode } & InputHTMLAttributes<HTMLInputElement>;
  export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function (
    { children, className = '', type: _, ...props },
    forwardedRef
  ) {
    const hiddenClassName = 'opacity-0 absolute w-full h-full top-0 left-0';
    const labelBaseClassName = 'relative flex gap-x-[0.5em] items-center';
    const labelBeforeClassName =
      "before:w-[20px] before:h-[20px] before:border-solid before:border-2 before:border-[color:#bbb] transition-all duration-[0.4s] before:bg-white before:rounded-[20%] before:content-[''] before:min-w-[20px]";
    const labelAfterClassName =
      "after:absolute after:transition-all after:duration-[0.4s] after:top-[calc(50%-25px/3)] after:left-[6.25px] after:w-[8px] after:h-[12px] after:border-solid after:border-transparent after:border-t-0 after:border-r-2 after:border-b-2 after:border-l-0 after:[transform:rotate(45deg)] after:content-['']";
    const checkedBeforeClassName =
      '[&:checked_~_span::before]:border-[color:#cf2a69] [&:checked_~_span::before]:bg-[color:#cf2a69]';
    const checkedAfterClassName = '[&:checked_~_span::after]:border-white';
    const rootBaseClassName = `relative ${className}`;
    const rootReadonlyClassName =
      props.readOnly || props.disabled ? 'opacity-40 cursor-default' : '';
    const rootClassName = `${rootBaseClassName} ${rootReadonlyClassName}`;
    return (
      <label className={rootClassName}>
        <input
          type="checkbox"
          className={`${hiddenClassName} ${checkedBeforeClassName} ${checkedAfterClassName}`}
          {...props}
          ref={forwardedRef}
        />
        <span className={`${labelBaseClassName} ${labelBeforeClassName} ${labelAfterClassName}`}>
          {children}
        </span>
      </label>
    );
  });

  type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;
  export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function (
    { className: additionalClassName = '', ...props },
    forwardedRef
  ) {
    const className = `p-[0.625em] text-[color:#555] bg-[color:#fff] border-solid border-2 border-[color:#ddd] rounded-[4px] [inline-size:100%] ${additionalClassName}`;
    return <textarea className={className} {...props} ref={forwardedRef} />;
  });

  /** `SyntheticEvent`ハンドラのショートハンド */
  type SyntheticEventHandler<T extends HTMLElement = HTMLElement> = (
    e: SyntheticEvent<T>
  ) => unknown;
  type Icon = 'add-date' | 'event-repeat' | 'pencil' | 'search' | 'create-file';
  type Size = 'small' | 'full' | 'free';
  type ButtonProps = (
    | ({ as: 'a' } & AnchorHTMLAttributes<HTMLAnchorElement>)
    | ({ as?: 'button' } & ButtonHTMLAttributes<HTMLButtonElement>)
    | ({ as: 'span' } & HTMLAttributes<HTMLSpanElement>)
    | ({ as: typeof Link } & LinkProps)
  ) & {
    icon?: Icon;
    size?: Size;
    disabled?: boolean;
    styleSubset?: 'normal' | 'clear';
  };
  /**
   * ボタン
   *
   * `onClick` と `onTouchEnd` 片方のみ指定の場合, 指定された `EventHandler` を双方に設定
   * @param props
   * @returns
   */
  export function Button(props: ButtonProps) {
    const {
      className: additionalClassName = '',
      size,
      icon,
      disabled = false,
      styleSubset = 'normal',
      ...asAndProps
    } = props;
    if (
      (asAndProps.onClick && !asAndProps.onTouchEnd) ||
      (!asAndProps.onClick && asAndProps.onTouchEnd)
    ) {
      asAndProps.onClick = asAndProps.onTouchEnd = (asAndProps.onClick ||
        asAndProps.onTouchEnd) as SyntheticEventHandler;
    }
    const baseClassName =
      'cursor-pointer min-w-[160px] min-h-[40px] py-[0.25em] px-[0.5em] flex items-center justify-center w-fit text-[color:rgb(255 255 255 / 90%)] no-underline whitespace-nowrap text-[#fff] bg-gradient-to-br from-0% to-100% from-[#f95d7a] to-[#d6308a] border-none rounded-full max-720:min-w-[100px] [&_input]:border-none';
    const mouseActionClassName = disabled
      ? '!opacity-60 !cursor-default'
      : 'hover:opacity-80 active:opacity-60';
    const sizeClassName = (() => {
      switch (size) {
        case 'small':
          return 'small';
        case 'free':
        case 'full':
          return `${size}-width` as const;
        default:
          return '';
      }
    })();
    const smallClassName =
      sizeClassName === 'small'
        ? '[&.small]:min-w-[120px] [&.small]:min-h-[30px] [&.small]:py-[0.125em] [&.small]:px-[0.25em] [&.small]:text-[length:0.875em]'
        : '';
    const fullWidthClassName =
      sizeClassName === 'full-width'
        ? '[&.full-width]:w-full [&.full-width]:min-w-[initial] [&.full-width]:min-h-[40px] [&.full-width]:py-[0.25em] [&.full-width]:px-[0.5em]'
        : '';
    const freeWidthClassName =
      sizeClassName === 'free-width'
        ? '[&.free-width]:w-full [&.free-width]:min-w-[initial] [&.free-width]:min-h-[40px] [&.free-width]:max-w-[160px] [&.free-width]:py-[0.25em] [&.free-width]:px-[0.5em]'
        : '';
    const subsetClassName =
      styleSubset === 'clear'
        ? '[&:not([disabled])]:bg-[color:white] [&:not([disabled])]:text-[color:#cf2a69] [&:not([disabled])]:border-[color:#cf2a69] [&:not([disabled])]:border-solid'
        : '';
    const iconClassName = icon ? (`with-icon ${icon}` as const) : '';
    const className = `${baseClassName} ${mouseActionClassName} ${smallClassName} ${fullWidthClassName} ${freeWidthClassName}  ${sizeClassName} ${subsetClassName} ${iconClassName} ${additionalClassName}`;
    if (asAndProps.as === 'a') {
      const { as: _, children, ...rest } = asAndProps;
      return (
        <a aria-label={`ボタン`} className={className} {...rest}>
          {children}
        </a>
      );
    } else if (asAndProps.as === 'button' || asAndProps.as === undefined) {
      const { as: _, type = 'button', ...rest } = asAndProps;
      return <button className={className} disabled={disabled} type={type} {...rest} />;
    } else if (asAndProps.as === 'span' || disabled) {
      const { as: _, ...rest } = asAndProps;
      return <span className={className} {...rest} />;
    } else {
      const { as: _, ...rest } = asAndProps;
      return <Link className={className} {...(rest as LinkProps)} />;
    }
  }

  type SelectableButtonProps = InputHTMLAttributes<HTMLInputElement>;
  export const SelectableButton = forwardRef<HTMLInputElement, SelectableButtonProps>(
    function SelectableButton(
      { className: additionalClassName = '', children: label, required, ...props },
      ref
    ) {
      const hiddenClassName = 'opacity-0 absolute w-full h-full top-0 left-0';
      const labelBaseClassName =
        'relative border border-[color:#cf2a69] bg-[color:white] text-[color:#cf2a69] text-center cursor-pointer px-[20px] py-[10px] rounded-[50px]';
      const selectedClassName =
        '[&:has(input:checked)]:bg-[color:#cf2a69] [&:has(input:checked)]:text-[color:white]';
      const labelClassName = `${labelBaseClassName} ${selectedClassName} ${additionalClassName}`;
      return (
        <label className={labelClassName}>
          <input
            type="checkbox"
            className={`${hiddenClassName} ${required ? 'hasRequired' : ''}`}
            required={required}
            {...props}
            ref={ref}
          />
          {label}
        </label>
      );
    }
  );
}
export const ContentsHandlerComponentsContext = React.createContext<{
  RequiredLabel: typeof Components.RequiredLabel;
  // FormUnit: typeof Components.FormUnit;
  // FormLabel: typeof Components.FormLabel;
  // Input: typeof Components.Input;
  // Select: typeof Components.Select;
  // Textarea: typeof Components.Textarea;
  // FileInput: typeof Components.FileInput;
  // Button: typeof Components.Button;
  // Checkbox: typeof Components.Checkbox;
}>(Components);

/** ユーティリティ関数群 */
export namespace Utils {
  /**
   * ランダムUUIDの生成
   * @returns
   */
  export function genUUID(): UUID {
    return window.crypto.randomUUID();
  }

  /**
   * フォームアイテムの最大apply_form_no取得
   * @returns
   */
  export function getMaxApplyFormNoFromPreviewItemList(previewItems: Types.PreviewItem[]) {
    const initMaxNo = Consts.RESERVED_APPLY_FORM_IDS.footer;
    if (previewItems.length <= 0) return initMaxNo;

    const targetPreviewItems = previewItems.filter((item) => item.element !== 'gohst');
    const applyFormNos: number[] = [];

    (
      targetPreviewItems.filter((item) => item !== null) as Exclude<
        Types.PreviewItem,
        Types.PreviewGohst
      >[]
    ).forEach((item) => {
      switch (item.element) {
        case 'template:consent': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'template:artist': {
          applyFormNos.push(item['kana.apply_form_no'] ?? 0);
          applyFormNos.push(item['name.apply_form_no'] ?? 0);
          break;
        }
        case 'template:venue': {
          applyFormNos.push(item['venue.apply_form_no'] ?? 0);
          if (!item['possibility.exclude']) {
            applyFormNos.push(item['possibility.apply_form_no'] ?? 0);
          }
          if (!item['note.exclude']) {
            applyFormNos.push(item['note.apply_form_no'] ?? 0);
          }
          break;
        }
        case 'template:address': {
          if (!item['postcode.exclude']) {
            applyFormNos.push(item['postcode.apply_form_no'] ?? 0);
          }
          if (!item['prefecture.exclude']) {
            applyFormNos.push(item['prefecture.apply_form_no'] ?? 0);
          }
          if (!item['city.exclude']) {
            applyFormNos.push(item['city.apply_form_no'] ?? 0);
          }
          if (!item['others.exclude']) {
            applyFormNos.push(item['others.apply_form_no'] ?? 0);
          }
          break;
        }
        case 'template:members': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'input': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'parallel-input': {
          applyFormNos.push(item['left.apply_form_no'] ?? 0);
          applyFormNos.push(item['right.apply_form_no'] ?? 0);
          break;
        }
        case 'check': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'category': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'dropdown-list': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'select-date': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'image': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'text': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
        case 'hr': {
          applyFormNos.push(item.apply_form_no ?? 0);
          break;
        }
      }
    });

    const tempMaxNo = Math.max(...applyFormNos);
    const maxNo = tempMaxNo <= initMaxNo ? initMaxNo : tempMaxNo;

    return maxNo;
  }

  /**
   * デフォルトのプレビューオブジェクトの最小限を生成
   * @param type アイテム種別
   * @param maxApplyFormNo フォームアイテムの最大apply_form_no
   * @param currentParams 保存済みフォームアイテム変換時用パラメータ。
   *
   * @returns
   */
  export function generateMinnimunPreviewItem(
    type: Types.PreviewItemType,
    maxApplyFormNo: number,
    currentParams?: { currentApplyFormNo: number; applyFormNos: number[] }
  ): Types.PreviewItem {
    const previewItemProps = { preview: true } as const;
    const currentApplyFormNo = currentParams ? currentParams.currentApplyFormNo : undefined;
    const currentApplyFormNoList = currentParams ? currentParams.applyFormNos : undefined;
    let applyFormNo = currentApplyFormNo ? currentApplyFormNo : maxApplyFormNo + 1;
    switch (type) {
      case 'check':
      case 'text':
      case 'input':
      case 'hr':
      case 'image':
      case 'select':
      case 'template:consent':
        return {
          uuid: genUUID(),
          element: type,
          apply_form_no: applyFormNo,
          ...previewItemProps,
        } as
          | Types.PreviewCheck
          | Types.PreviewText
          | Types.PreviewInput
          | Types.PreviewHorizontalRule
          | Types.PreviewImage
          | Types.PreviewSelectDate
          | Types.PreviewSelect
          | Types.PreviewTemplateConsent;
      case 'select-date':
        return {
          uuid: genUUID(),
          element: type,
          apply_form_no: applyFormNo,
          required: true,
          ...previewItemProps,
        } as Types.PreviewSelectDate;
      case 'template:venue':
        return {
          uuid: genUUID(),
          element: type,
          displayNameType: '0',
          'possibility.exclude': true,
          'note.exclude': true,
          'venue.apply_form_no': applyFormNo,
          'possibility.apply_form_no': currentApplyFormNoList?.includes(applyFormNo + 1)
            ? maxApplyFormNo + 1
            : applyFormNo + 1,
          'note.apply_form_no': currentApplyFormNoList?.includes(applyFormNo + 2)
            ? maxApplyFormNo + 2
            : applyFormNo + 2,
          ...previewItemProps,
        } as Types.PreviewTemplateVenue;
      case 'template:members':
        return {
          uuid: genUUID(),
          element: type,
          isGlobal: false,
          apply_form_no: applyFormNo,
          ...previewItemProps,
        } as Types.PreviewTemplateMembers;
      case 'template:address':
        return {
          uuid: genUUID(),
          element: type,
          'postcode.exclude': true,
          'prefecture.exclude': true,
          'city.exclude': true,
          'others.exclude': true,
          isGlobal: false,
          'postcode.apply_form_no': applyFormNo,
          'prefecture.apply_form_no': currentApplyFormNoList?.includes(applyFormNo + 1)
            ? maxApplyFormNo + 1
            : applyFormNo + 1,
          'city.apply_form_no': currentApplyFormNoList?.includes(applyFormNo + 2)
            ? maxApplyFormNo + 2
            : applyFormNo + 2,
          'others.apply_form_no': currentApplyFormNoList?.includes(applyFormNo + 3)
            ? maxApplyFormNo + 3
            : applyFormNo + 3,
          ...previewItemProps,
        } as Types.PreviewTemplateAddress;
      case 'template:artist':
        return {
          uuid: genUUID(),
          element: type,
          showAutoFill: true,
          'name.required': true,
          'kana.required': true,
          'name.apply_form_no': applyFormNo,
          'kana.apply_form_no': currentApplyFormNoList?.includes(applyFormNo + 1)
            ? maxApplyFormNo + 1
            : applyFormNo + 1,
          ...previewItemProps,
        } as Types.PreviewTemplateArtist;
      case 'input:url':
        return {
          uuid: genUUID(),
          element: 'input',
          type: 'url',
          apply_form_no: applyFormNo,
          ...previewItemProps,
        };
      case 'input:image':
        return {
          uuid: genUUID(),
          element: 'input',
          type: 'file',
          apply_form_no: applyFormNo,
          ...previewItemProps,
        };
      case 'parallel-input':
        return {
          uuid: genUUID(),
          element: type,
          'left.apply_form_no': applyFormNo,
          'right.apply_form_no': currentApplyFormNoList?.includes(applyFormNo + 1)
            ? maxApplyFormNo + 1
            : applyFormNo + 1,
          ...previewItemProps,
        };
      case 'dropdown-list':
        return { uuid: genUUID(), element: type, apply_form_no: applyFormNo, ...previewItemProps };
      case 'category':
        return { uuid: genUUID(), element: type, apply_form_no: applyFormNo, ...previewItemProps };
      default:
        return { uuid: genUUID(), element: 'input', ...previewItemProps };
    }
  }

  /**
   * デフォルトのプレビューオブジェクトを生成
   * @param type
   * @returns
   */
  export function generateDefaultPreviewItem(
    type: Types.PreviewItemType,
    langCodes: number[],
    maxApplyFormNo: number
  ): Types.PreviewItem {
    const minnimunPreviewItem = generateMinnimunPreviewItem(type, maxApplyFormNo);
    switch (type) {
      case 'input':
        return {
          ...minnimunPreviewItem,
          maxLength: '1000',
          lineBreaks: '1',
        } as Types.PreviewInput;
      case 'input:image':
        return {
          ...minnimunPreviewItem,
          fileType: 'image',
          ...langCodes.reduce<Pick<Types.PreviewInputFile, `placeholder.${string}`>>(
            (acc, lang) => {
              const isJa = isLangCodeJa(lang);
              if (isJa) {
                acc[`placeholder.${lang}`] = 'ファイルを選択';
                return acc;
              }
              acc[`placeholder.${lang}`] = 'Select a file';
              return acc;
            },
            {}
          ),
        } as Types.PreviewInputFile;
      case 'input:url':
        return {
          ...minnimunPreviewItem,
          maxLength: '500',
        } as Types.PreviewInputLink;
      case 'check':
        return {
          ...minnimunPreviewItem,
          labelAndValues: [{ value: '' }],
          direction: 'row',
          etcInput: true,
        } as Types.PreviewCheck;
      case 'select-date':
        return {
          ...minnimunPreviewItem,
          ...langCodes.reduce<Pick<Types.PreviewSelectDate, `label.text.${string}`>>(
            (acc, lang) => {
              const isJa = isLangCodeJa(lang);
              if (isJa) {
                acc[`label.text.${lang}`] = '生年月日';
                return acc;
              } else {
                acc[`label.text.${lang}`] = 'Date of Birth';
                return acc;
              }
            },
            {}
          ),
        } as Types.PreviewSelectDate;
      case 'template:artist':
        return {
          ...minnimunPreviewItem,
          ...langCodes.reduce<
            Pick<
              Types.PreviewTemplateArtist,
              `name.label.text.${string}` | `kana.label.text.${string}`
            >
          >((acc, lang) => {
            const isJa = isLangCodeJa(lang);
            if (isJa) {
              acc[`name.label.text.${lang}`] = 'アーティスト名';
              acc[`kana.label.text.${lang}`] = 'ヨミガナ';
              return acc;
            }
            acc[`name.label.text.${lang}`] = 'Artist Name';
            return acc;
          }, {}),
        } as Types.PreviewTemplateArtist;
      case 'template:address':
        return {
          ...minnimunPreviewItem,
          ...langCodes.reduce<
            Pick<
              Types.PreviewTemplateAddress,
              | `postcode.label.text.${string}`
              | `prefecture.label.text.${string}`
              | `city.label.text.${string}`
              | `others.label.text.${string}`
              | `postcode.placeholder.${string}`
              | `city.placeholder.${string}`
              | `others.placeholder.${string}`
            >
          >((acc, lang) => {
            const isJa = isLangCodeJa(lang);
            if (isJa) {
              acc[`postcode.label.text.${lang}`] = '郵便番号';
              acc[`prefecture.label.text.${lang}`] = '都道府県';
              acc[`city.label.text.${lang}`] = '市区町村';
              acc[`others.label.text.${lang}`] = '丁名番地 建物名・部屋番号';
              acc[`postcode.placeholder.${lang}`] = '郵便番号';
              acc[`city.placeholder.${lang}`] = '市区町村';
              acc[`others.placeholder.${lang}`] = '丁名番地 建物名・部屋番号';
              return acc;
            }
            acc[`postcode.label.text.${lang}`] = 'Postal code';
            acc[`prefecture.label.text.${lang}`] = 'Prefecture';
            acc[`city.label.text.${lang}`] = 'City';
            acc[`others.label.text.${lang}`] = 'Address line';
            acc[`postcode.placeholder.${lang}`] = 'Postal code';
            acc[`city.placeholder.${lang}`] = 'City';
            acc[`others.placeholder.${lang}`] = 'Address line';
            return acc;
          }, {}),
          'postcode.exclude': false,
          'prefecture.exclude': false,
          'city.exclude': false,
          'others.exclude': false,
        } as Types.PreviewTemplateAddress;
      case 'template:members':
        return {
          ...minnimunPreviewItem,
          'input1.exclude': false,
          'input2.exclude': false,
          'input3.exclude': false,
          'input4.exclude': false,
          'input5.exclude': false,
          'input6.exclude': false,
          ...langCodes.reduce<
            Pick<
              Types.PreviewTemplateMembers,
              | `${'input1' | 'input2' | 'input3' | 'input4' | 'input5' | 'input6'}.label.text.${string}`
              | `consent.label.text.${string}`
            >
          >((acc, lang) => {
            const isJa = isLangCodeJa(lang);
            if (isJa) {
              acc[`input1.label.text.${lang}`] = 'メンバー名';
              acc[`input2.label.text.${lang}`] = 'ヨミガナ';
              acc[`input3.label.text.${lang}`] = '年齢';
              acc[`input4.label.text.${lang}`] = '性別';
              acc[`input5.label.text.${lang}`] = 'パート';
              acc[`input6.label.text.${lang}`] = 'メンバー/サポートメンバー';
              acc[`consent.label.text.${lang}`] =
                '本オーディションへの応募およびメンバーに関する情報の提供について、各メンバー（未成年者の場合はその法定代理人）の同意を得ています。';
              return acc;
            }
            acc[`input1.label.text.${lang}`] = 'name';
            acc[`input2.label.text.${lang}`] = '';
            acc[`input3.label.text.${lang}`] = 'age';
            acc[`input4.label.text.${lang}`] = 'gender';
            acc[`input5.label.text.${lang}`] = 'part';
            acc[`input6.label.text.${lang}`] = 'Members/Support Members';
            return acc;
          }, {}),
        } as Types.PreviewTemplateMembers;
      case 'template:consent':
        return {
          ...minnimunPreviewItem,
          ...langCodes.reduce<Pick<Types.PreviewTemplateConsent, `placeholder.${string}`>>(
            (acc, lang) => {
              const isJa = isLangCodeJa(lang);
              if (isJa) {
                acc[`placeholder.${lang}`] = 'ファイルを選択';
                return acc;
              }
              acc[`placeholder.${lang}`] = 'Select a file';
              return acc;
            },
            {}
          ),
        } as Types.PreviewTemplateConsent;
      case 'template:venue':
        return {
          ...minnimunPreviewItem,
          examination_no: 3,
          'note.size': 1,
          'possibility.exclude': false,
          'note.exclude': false,
        } as Types.PreviewTemplateVenue;
      case 'dropdown-list':
        return {
          ...minnimunPreviewItem,
          etcInput: true,
          ...langCodes.reduce<Pick<Types.PreviewDropdownList, `labelAndValues`>>((acc, lang) => {
            const isJa = isLangCodeJa(lang);
            if (!acc.labelAndValues) {
              acc.labelAndValues = [{ value: '' }];
            }
            if (acc.labelAndValues) {
              if (isJa) {
                acc['labelAndValues'][0][`label.${lang}`] = '未選択';
                return acc;
              }
              acc['labelAndValues'][0][`label.${lang}`] = 'Not selected';
              return acc;
            }
            return acc;
          }, {}),
        } as Types.PreviewDropdownList;
      case 'category':
        return {
          ...minnimunPreviewItem,
          labelAndValues: [{ value: '' }],
          direction: 'row',
        } as Types.PreviewCategory;
      default:
        return minnimunPreviewItem;
    }
  }
  /**
   * デフォルトのオブジェクトを生成
   * @param type
   * @returns
   */
  export function generateDefaultItem(type: Types.ItemType): Types.Item {
    switch (type) {
      case 'check':
        return { uuid: genUUID(), preview: false, element: 'check', etcInput: true };
      case 'text':
      case 'input':
      case 'hr':
      case 'image':
      case 'select-date':
      case 'select':
        return { uuid: genUUID(), preview: false, element: type } as Types.Item;
      case 'template:consent':
        return { uuid: genUUID(), preview: false, element: type } as Types.TemplateConsent;
      case 'template:members':
        return {
          uuid: genUUID(),
          preview: false,
          element: type,
          isGlobal: false,
        } as Types.TemplateMembers;
      case 'template:address':
        return {
          uuid: genUUID(),
          preview: false,
          element: type,
          'postcode.exclude': true,
          'prefecture.exclude': true,
          'city.exclude': true,
          'others.exclude': true,
          isGlobal: false,
        } as Types.TemplateAddress;
      case 'template:venue':
        return {
          uuid: genUUID(),
          preview: false,
          element: type,
          displayNameType: '0',
          'possibility.exclude': true,
          'note.exclude': true,
        } as Types.TemplateVenue;
      case 'template:artist':
        return { uuid: genUUID(), preview: false, element: type, showAutoFill: true } as Types.Item;
      case 'input:url':
        return { uuid: genUUID(), preview: false, element: 'input', type: 'url' };
      case 'input:image':
        return { uuid: genUUID(), preview: false, element: 'input', type: 'file' };
      case 'parallel-input':
        return { uuid: genUUID(), preview: false, element: type };
      case 'dropdown-list':
        return { uuid: genUUID(), preview: false, element: 'dropdown-list', etcInput: true };
      case 'category':
        return { uuid: genUUID(), preview: false, element: 'category' };
      default:
        return { uuid: genUUID(), preview: false, element: 'input' };
    }
  }

  /**
   * `(テンプレート/下書).style`をオブジェクトに変換
   * @param style
   * @returns
   */
  export function parseStyle(style: string | null | undefined): Record<string, string | undefined> {
    if (!style) return {};
    return style.split('; ').reduce<Record<string, string | undefined>>((prev, current) => {
      const [key, value] = current.split(': ');
      prev[key] = value;
      return prev;
    }, {});
  }

  /**
   * ファイルオブジェクトをBase64エンコード
   * @param file
   * @returns
   */
  export function file2Base64(file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const base64 = e.target?.result?.toString();
          if (!base64) {
            throw new Error('base64失敗');
          }
          resolve(base64);
        } catch (e) {
          reject('base64変換失敗');
        }
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * 管理画面でのみ利用可能な関数
   * ファイルオブジェクトをS3にアップロード
   * @param file
   * @returns アップロード後のS3 Object URL
   */
  export async function uploadFile2S3(
    file: File,
    param:
      | { audition_id: string; examination_no: number }
      | { template_id: string; examination_no: number }
  ): Promise<string> {
    const { APIHandler } = await import('./../api/APIHandler');
    const params =
      'audition_id' in param
        ? ({
            upload_kbn: '0',
            audition_id: param.audition_id,
            examination_no: param.examination_no,
            file_name: file.name,
            overseas: '0',
          } as const) //overseas固定
        : ({
            upload_kbn: '1',
            template_id: param.template_id,
            examination_no: param.examination_no,
            file_name: file.name,
            overseas: '0',
          } as const); //overseas固定
    const { url: uploadUrl, key } = await APIHandler.M002A12(params);
    await fetch(uploadUrl, { method: 'PUT', body: file });
    // 海外への登録処理
    const overseasParams =
      'audition_id' in param
        ? ({
            upload_kbn: '0',
            audition_id: param.audition_id,
            examination_no: param.examination_no,
            file_name: file.name,
            overseas: '1',
          } as const) //overseas固定
        : ({
            upload_kbn: '1',
            template_id: param.template_id,
            examination_no: param.examination_no,
            file_name: file.name,
            overseas: '1',
          } as const); //overseas固定
    const { url: overseasUploadUrl } = await APIHandler.M002A12(overseasParams);
    await fetch(overseasUploadUrl, { method: 'PUT', body: file });
    return `/${key}`;
  }

  export function isLangCodeJa(lang: string | number): boolean {
    return lang === '0' || lang === 0;
  }
}

/** 定数群 */
export namespace Consts {
  /** 各種定数値 */
  export const DEFAULT_VALUES = {
    IMAGE_WIDTH: '200',
    IMAGE_HEIGHT: '200',
    TEXT_COLOR: '#000000',
  } as const;

  /** 各種固定プロパティ名 */
  export const PROP_NAMES = {
    TEXT: {
      DECORATION: {
        UNDERLINE: 'underlined',
        BOLD: 'bold',
        ITALIC: 'italic',
      },
      ALIGN: {
        LEFT: 'left',
        RIGHT: 'right',
        CENTER: 'center',
      },
    },
  } as const;

  /** チェックボックス/ラジオボタン:配置方向 */
  export const DIRECTION = {
    COL: '0',
    ROW: '1',
  } as const;

  /** 必須フラグ */
  export const REQUIRED = {
    TRUE: '1',
    FALSE: '0',
  } as const;

  /** 表示フラグ */
  export const SHOW = {
    TRUE: '1',
    FALSE: '0',
  } as const;

  /** その他入力欄フラグ */
  export const ETCINPUT = {
    TRUE: '1',
    FALSE: '0',
  } as const;

  /** 入力欄:入力制限 */
  export const VALIDATION = {
    /** 制限なし */
    NONE: '0',
    /** 半角数字のみ */
    NUMERIC: '1',
    /** 半角のみ */
    HALF: '2',
  } as const;

  /** 希望会場：選択肢の表示形式 */
  export const VENUE_DISPLAY_TYPE = {
    /** 審査名 */
    TITLE: '0',
    /** 会場 */
    VENUE: '1',
  } as const;

  /** 編集可能プロパティ */
  export const EDITABLE_PROPS = {
    /** テキスト要素編集可能プロパティ一覧 */
    text: ['required', 'text', 'alignment', 'size', 'color', 'formats'],
    /** input要素編集可能プロパティ一覧 */
    input: [
      'required',
      'placeholder',
      'lineBreaks',
      'maxLength',
      'fileType',
      'validation',
      'validation',
    ],
    /** 画像要素編集可能プロパティ一覧 */
    image: ['src', 'width', 'height', 'alt'],
    /** 横線要素編集可能プロパティ一覧 */
    hr: ['width', 'color'],
    /** チェックボックス/ラジオボタン編集可能プロパティ一覧 */
    check: ['required', 'selectable', 'etcInput', 'labelAndValues', 'direction'],
    /** 日付選択編集可能プロパティ一覧 */
    'select-date': ['required', 'exclude', 'year-range'],
    /** テンプレート:アーティスト:編集可能プロパティ一覧 */
    'template:artist': [
      'showAutoFill',
      'name.label.text',
      'kana.label.text',
      'name.label.color',
      'kana.label.color',
      'name.placeholder',
      'kana.placeholder',
    ],
    /** テンプレート:会場:編集可能プロパティ一覧 */
    'template:venue': [
      'required',
      'label.text',
      'label.color',
      'possibility.exclude',
      'possibility.text',
      'possibility.color',
      'note.exclude',
      'note.text',
      'note.color',
      'note.size',
      'examination_no',
      'displayNameType',
      'labelAndValues',
    ],
    /** テンプレート:住所:編集可能プロパティ一覧 */
    'template:address': [
      'postcode.exclude',
      'postcode.required',
      'postcode.label.color',
      'postcode.placeholder',
      'prefecture.exclude',
      'prefecture.required',
      'prefecture.label.color',
      'city.exclude',
      'city.required',
      'city.label.color',
      'city.placeholder',
      'others.exclude',
      'others.required',
      'others.label.color',
      'others.placeholder',
    ],
    /** テンプレート:同意:編集可能プロパティ一覧 */
    'template:consent': ['required', 'label.text', 'label.color', 'placeholder'],
    /** テンプレート:メンバー情報:編集可能プロパティ一覧 */
    'template:members': [
      'input1.exclude',
      'input1.label.color',
      'input1.label.text',
      'input1.placeholder',
      'input2.exclude',
      'input2.label.color',
      'input2.label.text',
      'input2.placeholder',
      'input3.exclude',
      'input3.label.color',
      'input3.label.text',
      'input3.placeholder',
      'input4.exclude',
      'input4.label.color',
      'input4.label.text',
      'input5.exclude',
      'input5.label.color',
      'input5.label.text',
      'input5.placeholder',
      'input6.exclude',
      'input6.label.color',
      'input6.label.text',
    ],
    'parallel-input': [
      'right.required',
      'right.label.text',
      'right.label.color',
      'right.placeholder',
      'left.required',
      'left.label.text',
      'left.label.color',
      'left.placeholder',
    ],
    'dropdown-list': ['required', 'label.text', 'label.color', 'etcInput', 'labelAndValues'],
    category: [
      'required',
      'label.text',
      'label.color',
      'selectable',
      'labelAndValues',
      'direction',
    ],
  } as const satisfies Record<string, readonly string[]>;

  /** システムで固定値を使うため、予約している応募フォームID */
  export const RESERVED_APPLY_FORM_IDS = {
    /* ヘッダ	応募フォームのヘッダを設定する。 */
    // @deprecated ヘッダーは廃止
    _header: 1,
    /* 本体	応募フォームの本体を設定する。 */
    body: 2,
    /* フッタ	応募フォームのフッタを設定する。 */
    footer: 3,
  } as const;

  /** テンプレート:メンバー情報:追加可能なメンバー数 */
  export const TEMPLATE_MEMBERS_MAX_NUM = 20;
  /** テンプレート:メンバー情報:性別:選択値 */
  export const TEMPLATE_MEMBERS_GENDER_VALUES = {
    NONE: 'CH_007',
    MEN: 'CH_008',
    WOMEN: 'CH_009',
  } as const;
  /** テンプレート:メンバー情報:応募方法:選択値 */
  export const TEMPLATE_MEMBERS_APPLY_TYPE_VALUES = {
    SOLO: 'CH_015',
    GROUP: 'CH_016',
  } as const;
  /** テンプレート:メンバー情報:メンバー種別:選択値 */
  export const TEMPLATE_MEMBERS_MEMBER_TYPE_VALUES = {
    MEMBERS: 'CH_017',
    SUPPORT_MEMBERS: 'CH_018',
  } as const;
  /** テンプレート:メンバー情報:性別:選択肢 */
  export const TEMPLATE_MEMBERS_GENDER_OPTIONS = [
    TEMPLATE_MEMBERS_GENDER_VALUES.NONE,
    TEMPLATE_MEMBERS_GENDER_VALUES.MEN,
    TEMPLATE_MEMBERS_GENDER_VALUES.WOMEN,
  ] as const;
  /** テンプレート:メンバー情報:応募方法:選択肢 */
  export const TEMPLATE_MEMBERS_APPLY_TYPE_OPTIONS = [
    TEMPLATE_MEMBERS_APPLY_TYPE_VALUES.GROUP,
    TEMPLATE_MEMBERS_APPLY_TYPE_VALUES.SOLO,
  ] as const;
  /** テンプレート:メンバー情報:メンバー種別:選択肢 */
  export const TEMPLATE_MEMBERS_MEMBER_TYPE_OPTIONS = [
    TEMPLATE_MEMBERS_MEMBER_TYPE_VALUES.MEMBERS,
    TEMPLATE_MEMBERS_MEMBER_TYPE_VALUES.SUPPORT_MEMBERS,
  ] as const;

  // 半角数字のみ：正規表現
  export const NUMERIC_REGEX = /^[0-9]+$/;
  export const NUMERIC_AND_LINEBREAK_REGEX = /^[0-9\n]+$/;
  // 半角のみ：正規表現
  export const HALF_REGEX = /^[ -~]+$/;
  export const HALF_AND_LINEBREAK_REGEX = /^ -~\n]+$/;
  // 全角カナのみ：正規表現
  export const KANA_REGEX = /^[ァ-ヴー　]+$/;
  // 制限なし：正規表現
  export const NONE_REGEX = /^[^\u{10000}-\u{10FFFF}]+$/u;
  export const NONE_AND_LINEBREAK_REGEX = /^[^\u{10000}-\u{10FFFF}\n]+$/u;
}

/** 型定義 */
export namespace Types {
  export type ValueOf<T extends object> = T[keyof T];

  /** テキスト配置 */
  export type TextAlign = ValueOf<typeof Consts.PROP_NAMES.TEXT.ALIGN>;
  /** テキスト装飾 */
  export type TextDecoration = ValueOf<typeof Consts.PROP_NAMES.TEXT.DECORATION>;

  /** 日付範囲 */
  export type DateRange = {
    from?: number;
    to?: number;
  };
  /** 言語コード */
  export type LangCode = string;
  /** テキスト要素の編集可能プロパティ */
  export type EditableTextProps =
    | Exclude<(typeof Consts.EDITABLE_PROPS.text)[number], 'text'>
    | `text.${LangCode}`;
  /** input要素の編集可能プロパティ */
  export type EditableInputProps =
    | Exclude<(typeof Consts.EDITABLE_PROPS.input)[number], 'placeholder'>
    | `placeholder.${LangCode}`;
  /** input:label要素の編集可能プロパティ */
  export type EditableInputLabelProps = `label.${EditableTextProps}`;
  /** 画像要素の編集可能プロパティ */
  export type EditableImageProps = (typeof Consts.EDITABLE_PROPS.image)[number];
  /** 横線要素編集可能プロパティ */
  export type EditableHrProps = (typeof Consts.EDITABLE_PROPS.hr)[number];
  /** チェックボックス/ラジオボタン編集可能プロパティ */
  export type EditableCheckProps = (typeof Consts.EDITABLE_PROPS.check)[number];
  /** 日付選択編集可能プロパティ */
  export type EditableSelectDateProps = (typeof Consts.EDITABLE_PROPS)['select-date'][number];

  /** テキスト要素の設定情報(inputのlabelにも流用) */
  export type TextConfig = Partial<{
    [K in EditableTextProps]: string;
  }>;

  /** プレビュー要素:ベース */
  export type PreviewBase = { uuid: UUID; focus?: boolean; preview: true };
  /** 要素:ベース */
  type Base = { uuid: UUID; focus?: boolean; preview: false };
  /** プレビュー要素:フォームパーツ */
  type PreviewFormItem = { required?: boolean; apply_form_no?: number };
  /** 要素:フォームパーツ */
  type FormItem = { required?: boolean; apply_form_no?: number; sort_no?: number };
  /** プレビュー要素:仮オブジェクト */
  export type PreviewGohst = PreviewBase & { element: 'gohst'; type?: PreviewItemElement };
  /** プレビュー要素:テキスト */
  export type PreviewText = PreviewBase &
    PreviewFormItem & {
      element: 'text';
    } & TextConfig;
  /** 要素:テキスト */
  export type Text = Base & {
    element: 'text';
  } & TextConfig;
  /** プレビュー要素:入力欄 */
  export type PreviewInput = PreviewBase &
    PreviewFormItem & {
      element: 'input';
      type?: 'text';
    } & {
      [K in Exclude<EditableInputProps, keyof PreviewFormItem>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:入力欄 */
  export type Input = Base &
    FormItem & {
      element: 'input';
      type?: 'text';
    } & {
      [K in Exclude<EditableInputProps, keyof FormItem>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:入力欄(URL) */
  export type PreviewInputLink = PreviewBase &
    PreviewFormItem & {
      element: 'input';
      type: 'url';
    } & {
      [K in Extract<EditableInputProps, `placeholder.${string}` | 'maxLength'>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:入力欄(URL) */
  export type InputLink = Base &
    FormItem & {
      element: 'input';
      type: 'url';
    } & {
      [K in Extract<EditableInputProps, `placeholder.${string}` | 'maxLength'>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:入力欄(ファイル) */
  export type PreviewInputFile = PreviewBase &
    PreviewFormItem & {
      element: 'input';
      type: 'file';
      fileType?: 'image' | 'video' | 'audio';
    } & {
      [K in Extract<EditableInputProps, `placeholder.${string}`>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:入力欄(ファイル) */
  export type InputFile = Base &
    FormItem & {
      element: 'input';
      type: 'file';
      fileType?: 'image' | 'video' | 'audio';
    } & {
      [K in Extract<EditableInputProps, `placeholder.${string}`>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:画像 */
  export type PreviewImage = PreviewBase &
    PreviewFormItem & {
      element: 'image';
    } & {
      [K in EditableImageProps]?: string;
    };
  /** 要素:画像 */
  export type Image = Base & {
    element: 'image';
  } & {
    [K in EditableImageProps]?: string;
  };
  /** プレビュー要素:区切り線 */
  export type PreviewHorizontalRule = PreviewBase &
    PreviewFormItem & {
      element: 'hr';
    } & {
      [K in EditableHrProps]?: string;
    };
  /** 要素:区切り線 */
  export type HorizontalRule = Base & {
    element: 'hr';
  } & {
    [K in EditableHrProps]?: string;
  };
  /** プレビュー要素:チェックボックス/ラジオボタン */
  export type PreviewCheck = PreviewBase &
    PreviewFormItem & {
      element: 'check';
    } & {
      [K in Extract<EditableCheckProps, 'selectable'>]?: string;
    } & {
      [K in Extract<EditableCheckProps, 'etcInput'>]?: boolean;
    } & {
      [K in Extract<EditableCheckProps, 'direction'>]?: 'row' | 'col';
    } & {
      [K in Extract<EditableCheckProps, 'labelAndValues'>]?: ({ value: string } & {
        [K in `label.${string}`]?: string;
      })[];
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:チェックボックス/ラジオボタン */
  export type Check = Base &
    FormItem & {
      element: 'check';
    } & {
      [K in Extract<EditableCheckProps, 'selectable'>]?: string;
    } & {
      [K in Extract<EditableCheckProps, 'etcInput'>]?: boolean;
    } & {
      [K in Extract<EditableCheckProps, 'direction'>]?: 'row' | 'col';
    } & {
      [K in Extract<EditableCheckProps, 'labelAndValues'>]?: ({ value: string } & {
        [K in `label.${string}`]?: string;
      })[];
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:日付選択 */
  export type PreviewSelectDate = PreviewBase &
    PreviewFormItem & {
      element: 'select-date';
    } & {
      /**
       * 下位ビットから日/月/年の除外フラグ
       *
       * ex. 0b011(=3)なら年と月を除外
       */
      [K in Extract<EditableSelectDateProps, 'exclude'>]?: number;
    } & {
      [K in Extract<EditableSelectDateProps, 'year-range'>]?: DateRange;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:日付選択 */
  export type SelectDate = Base &
    FormItem & {
      element: 'select-date';
    } & {
      /**
       * 下位ビットから日/月/年の除外フラグ
       *
       * ex. 0b011(=3)なら年と月を除外
       */
      [K in Extract<EditableSelectDateProps, 'exclude'>]?: number;
    } & {
      [K in Extract<EditableSelectDateProps, 'year-range'>]?: DateRange;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:選択肢 */
  export type PreviewSelect = PreviewBase &
    PreviewFormItem & {
      element: 'select';
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:選択肢 */
  export type Select = Base &
    FormItem & {
      element: 'select';
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:テンプレート:アーティスト情報 */
  export type PreviewTemplateArtist = PreviewBase & {
    element: 'template:artist';
    showAutoFill?: boolean;
    'name.label.color'?: string;
    'name.required'?: boolean;
    'name.apply_form_no'?: number;
    'kana.label.color'?: string;
    'kana.required'?: boolean;
    'kana.apply_form_no'?: number;
    [K: `${'name' | 'kana'}.${'label.text' | 'placeholder'}.${LangCode}`]: string;
  };
  /** 要素:テンプレート:アーティスト情報 */
  export type TemplateArtist = Base & {
    element: 'template:artist';
    showAutoFill?: boolean;
    'name.label.color'?: string;
    'name.required'?: boolean;
    'name.apply_form_no'?: number;
    'kana.label.color'?: string;
    'kana.required'?: boolean;
    'kana.apply_form_no'?: number;
    [K: `${'name' | 'kana'}.${'label.text' | 'placeholder'}.${LangCode}`]: string;
  };
  /** プレビュー要素:テンプレート:希望会場 */
  export type PreviewTemplateVenue = PreviewBase & {
    element: 'template:venue';
    required?: boolean;
    'venue.apply_form_no'?: number;
    'label.color'?: string;
    labelAndValues?: { value: string; label: string }[];
    examination_no?: number;
    displayNameType?: '0' | '1';
    'possibility.apply_form_no'?: number;
    'possibility.exclude'?: boolean;
    'possibility.color'?: string;
    'note.apply_form_no'?: number;
    'note.exclude'?: boolean;
    'note.color'?: string;
    'note.size'?: number;
    [K: `${'label' | 'possibility' | 'note'}.text.${LangCode}`]: string;
  };
  /** 要素:テンプレート:希望会場 */
  export type TemplateVenue = Base & {
    element: 'template:venue';
    required?: boolean;
    'venue.apply_form_no'?: number;
    'label.color'?: string;
    labelAndValues?: { value: string; label: string }[];
    examination_no?: number;
    displayNameType?: '0' | '1';
    'possibility.apply_form_no'?: number;
    'possibility.exclude'?: boolean;
    'possibility.color'?: string;
    'note.apply_form_no'?: number;
    'note.exclude'?: boolean;
    'note.color'?: string;
    'note.size'?: number;
    [K: `${'label' | 'possibility' | 'note'}.text.${LangCode}`]: string;
  };
  /** プレビュー要素:テンプレート:住所 */
  export type PreviewTemplateAddress = PreviewBase & {
    element: 'template:address';
    isGlobal: boolean;
    'postcode.exclude'?: boolean;
    'postcode.required'?: boolean;
    'postcode.label.color'?: string;
    'postcode.apply_form_no'?: number;
    'prefecture.exclude'?: boolean;
    'prefecture.required'?: boolean;
    'prefecture.apply_form_no'?: number;
    'prefecture.label.color'?: string;
    'prefecture.labelAndValues'?: { value: string; label: string }[];
    'city.exclude'?: boolean;
    'city.required'?: boolean;
    'city.apply_form_no'?: number;
    'city.label.color'?: string;
    'others.exclude'?: boolean;
    'others.required'?: boolean;
    'others.apply_form_no'?: number;
    'others.label.color'?: string;
    [K: `${'postcode' | 'city' | 'others'}.placeholder.${LangCode}`]: string;
    [K: `${'postcode' | 'prefecture' | 'city' | 'others'}.label.text.${LangCode}`]: string;
  };
  /** 要素:テンプレート:住所 */
  export type TemplateAddress = Base & {
    element: 'template:address';
    isGlobal: boolean;
    'postcode.exclude'?: boolean;
    'postcode.required'?: boolean;
    'postcode.apply_form_no'?: number;
    'postcode.label.color'?: string;
    'prefecture.exclude'?: boolean;
    'prefecture.required'?: boolean;
    'prefecture.apply_form_no'?: number;
    'prefecture.label.color'?: string;
    'prefecture.labelAndValues'?: { value: string; label: string }[];
    'city.exclude'?: boolean;
    'city.required'?: boolean;
    'city.apply_form_no'?: number;
    'city.label.color'?: string;
    'others.exclude'?: boolean;
    'others.required'?: boolean;
    'others.apply_form_no'?: number;
    'others.label.color'?: string;
    [K: `${'postcode' | 'city' | 'others'}.placeholder.${LangCode}`]: string;
    [K: `${'postcode' | 'prefecture' | 'city' | 'others'}.label.text.${LangCode}`]: string;
  };
  /** プレビュー要素:テンプレート:同意書 */
  export type PreviewTemplateConsent = PreviewBase &
    PreviewFormItem & {
      element: 'template:consent';
    } & {
      [K in Extract<EditableInputProps, `placeholder.${string}`>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:テンプレート:同意書 */
  export type TemplateConsent = Base &
    FormItem & {
      element: 'template:consent';
    } & {
      [K in Extract<EditableInputProps, `placeholder.${string}`>]?: string;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:テンプレート:メンバー情報 */
  export type PreviewTemplateMembers = PreviewBase &
    PreviewFormItem & {
      element: 'template:members';
      isGlobal: boolean;
      'input1.exclude'?: boolean;
      [K: `input1.label.text.${LangCode}`]: string;
      'input1.label.color'?: string;
      'input2.exclude'?: boolean;
      [K: `input2.label.text.${LangCode}`]: string;
      'input2.label.color'?: string;
      'input3.exclude'?: boolean;
      [K: `input3.label.text.${LangCode}`]: string;
      'input3.label.color'?: string;
      'input4.exclude'?: boolean;
      [K: `input4.label.text.${LangCode}`]: string;
      'input4.label.color'?: string;
      'input5.exclude'?: boolean;
      [K: `input5.label.text.${LangCode}`]: string;
      'input5.label.color'?: string;
      'input6.exclude'?: boolean;
      [K: `input6.label.text.${LangCode}`]: string;
      'input6.label.color'?: string;
      [K: `${'input1' | 'input2' | 'input3' | 'input5'}.placeholder.${LangCode}`]: string;
      [K: `consent.label.text.${LangCode}`]: string;
    };
  /** 要素:テンプレート:メンバー情報 */
  export type TemplateMembers = Base &
    FormItem & {
      element: 'template:members';
      isGlobal: boolean;
      'input1.exclude'?: boolean;
      [K: `input1.label.text.${LangCode}`]: string;
      'input1.label.color'?: string;
      'input2.exclude'?: boolean;
      [K: `input2.label.text.${LangCode}`]: string;
      'input2.label.color'?: string;
      'input3.exclude'?: boolean;
      [K: `input3.label.text.${LangCode}`]: string;
      'input3.label.color'?: string;
      'input4.exclude'?: boolean;
      [K: `input4.label.text.${LangCode}`]: string;
      'input4.label.color'?: string;
      'input5.exclude'?: boolean;
      [K: `input5.label.text.${LangCode}`]: string;
      'input5.label.color'?: string;
      'input6.exclude'?: boolean;
      [K: `input6.label.text.${LangCode}`]: string;
      'input6.label.color'?: string;
      [K: `${'input1' | 'input2' | 'input3' | 'input5'}.placeholder.${LangCode}`]: string;
      [K: `consent.label.text.${LangCode}`]: string;
    };
  /** プレビュー要素:横並び入力欄 */
  export type PreviewParallelInput = PreviewBase & {
    element: 'parallel-input';
    [K: `right.label.text.${LangCode}`]: string;
    [K: `right.placeholder.${LangCode}`]: string;
    'right.label.color'?: string;
    [K: `left.label.text.${LangCode}`]: string;
    [K: `left.placeholder.${LangCode}`]: string;
    'left.label.color'?: string;
  } & {
    [K in `${'right' | 'left'}.${keyof PreviewFormItem}`]?: K extends `${'right' | 'left'}.${infer R extends keyof PreviewFormItem}`
      ? PreviewFormItem[R]
      : never;
  };
  /** 要素:横並び入力欄 */
  export type ParallelInput = Base & {
    element: 'parallel-input';
    [K: `right.label.text.${LangCode}`]: string;
    [K: `right.placeholder.${LangCode}`]: string;
    'right.label.color'?: string;
    [K: `left.label.text.${LangCode}`]: string;
    [K: `left.placeholder.${LangCode}`]: string;
    'left.label.color'?: string;
  } & {
    [K in `${'right' | 'left'}.${keyof FormItem}`]?: K extends `${'right' | 'left'}.${infer R extends keyof FormItem}`
      ? FormItem[R]
      : never;
  };
  /** プレビュー要素:ドロップダウンリスト */
  export type PreviewDropdownList = PreviewBase &
    PreviewFormItem & {
      element: 'dropdown-list';
      labelAndValues?: ({ value: string } & {
        [K in `label.${string}`]?: string;
      })[];
    } & {
      [K in Extract<EditableCheckProps, 'etcInput'>]?: boolean;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** 要素:ドロップダウンリスト */
  export type DropdownList = Base &
    FormItem & {
      element: 'dropdown-list';
      labelAndValues?: ({ value: string } & {
        [K in `label.${string}`]?: string;
      })[];
    } & {
      [K in Extract<EditableCheckProps, 'etcInput'>]?: boolean;
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    };
  /** プレビュー要素:カテゴリー */
  export type PreviewCategory = PreviewBase &
    PreviewFormItem & {
      element: 'category';
    } & {
      [K in Extract<EditableCheckProps, 'selectable'>]?: string;
    } & {
      [K in Extract<EditableCheckProps, 'direction'>]?: 'row' | 'col';
    } & {
      [K in Extract<EditableCheckProps, 'labelAndValues'>]?: ({ value: string } & {
        [K in `label.${string}`]?: string;
      })[];
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    } & {
      [K in `${keyof PreviewFormItem}.${'etc'}`]?: K extends `${'etc'}.${infer R extends keyof PreviewFormItem}`
        ? PreviewFormItem[R]
        : never;
    };
  export type Category = Base &
    FormItem & {
      element: 'category';
    } & {
      [K in Extract<EditableCheckProps, 'selectable'>]?: string;
    } & {
      [K in Extract<EditableCheckProps, 'direction'>]?: 'row' | 'col';
    } & {
      [K in Extract<EditableCheckProps, 'labelAndValues'>]?: ({ value: string } & {
        [K in `label.${string}`]?: string;
      })[];
    } & {
      [K in EditableInputLabelProps]?: K extends `label.${infer R extends EditableTextProps}`
        ? TextConfig[R]
        : never;
    } & {
      [K in `${keyof FormItem}.${'etc'}`]?: K extends `${'etc'}.${infer R extends keyof FormItem}`
        ? FormItem[R]
        : never;
    };
  /** 要素：カテゴリー */
  /** プレビュー要素:ドラッグ中アイテムデータ */
  export type PreviewItem =
    | PreviewTemplateConsent
    | PreviewTemplateArtist
    | PreviewTemplateVenue
    | PreviewTemplateAddress
    | PreviewTemplateMembers
    | PreviewGohst
    | PreviewText
    | PreviewInput
    | PreviewInputLink
    | PreviewInputFile
    | PreviewImage
    | PreviewCheck
    | PreviewSelectDate
    | PreviewSelect
    | PreviewParallelInput
    | PreviewDropdownList
    | PreviewCategory
    | PreviewHorizontalRule;
  export type Item =
    | TemplateConsent
    | TemplateVenue
    | TemplateArtist
    | TemplateAddress
    | TemplateMembers
    | Text
    | Input
    | InputLink
    | InputFile
    | Image
    | Check
    | SelectDate
    | Select
    | ParallelInput
    | DropdownList
    | Category
    | HorizontalRule;
  /** プレビュー要素:種類 */
  export type PreviewItemElement = PreviewItem['element'];
  export type PreviewItemType = PreviewItemElement | 'input:url' | 'input:image';
  /** 要素:種類 */
  export type ItemElement = Item['element'];
  export type ItemType = ItemElement | 'input:url' | 'input:image';

  /** 応募フォームレイアウト:多言語対応プロパティ */
  type ApplyFormMultilingualProp =
    | 'question'
    | 'place_holder'
    | 'choices'
    | 'general1'
    | 'general2'
    | 'general3';
  /** 応募フォームレイアウト情報 */
  export type ApplyForm = {
    template_id: string;
    examination_no: number;
    apply_form_no: number;
    format: string;
    sort_no: number;
  } & Partial<{
    sync: boolean;
    required: string;
    value: string;
    styles: Record<string, string | undefined>;
    general1: string;
    general2: string;
    general3: string;
  }> & {
      [K in ApplyFormMultilingualProp as `${K}.${string | number}`]: string;
    };

  /** オーディション基本情報_テンプレート */
  // type AuditionSettingTemplate = APIResult.M002A04['audition_template'][number];
  type AuditionSettingTemplate = {
    /** テンプレートID */
    template_id: string;
    /** テンプレート名 */
    template_name: string;
    /** テンプレートメモ */
    template_memo: string;
    /** 言語 */
    lang: string;
    /** タイトル */
    title: string;
    /** 所属 */
    sgroup: number;
    /** URL1 */
    url1: string;
    /** URL2 */
    url2: string;
    /** サンプルURL */
    sample_url: string;
    /** 公開開始日時 */
    start_dtm: Date;
    /** 公開終了日時 */
    end_dtm: Date;
    /** ドメイン指定解除テキスト */
    domain_txt: string;
    /** 完了画面カスタムテキスト */
    end_custom_txt: string;
    /** 応募完了メール・件名 */
    mail_title: string;
    /** 応募完了メール・送信元 */
    mail_from: string;
    /** 応募完了メール・本文 */
    mail_txt: string;
    /** 受付前テキスト */
    start_txt: string;
    /** 終了後テキスト */
    end_txt: string;
    /** 有効フラグ */
    valid_flg: string;
    /** 表示言語 */
    display_lang: string;
    /** デフォルト表示言語 */
    def_lang: string;
    /** コアスタッフ */
    core_staff: string;
    /** 作成者 */
    author: string;
    /** ステータス */
    status: string;
    /** LPURL */
    lp_url: string;
    /** GoogleTag */
    googletag: string;
    /** GoogleTag2 */
    googletag2: string;
    /** 募集ページ画像 */
    image: string;
    /** 募集ページ詳細 */
    detail: string;
    /** グループ応募可否 */
    group_ap: number;
    /** 特定利用 */
    specific_use: string;
    /** プライバシーポリシー */
    privacy_policy: string;
    /** オーディション応募規約 */
    terms: string;
    /** リビジョン */
    revision: number;
  };
  /** 審査情報_テンプレート */
  // type ExaminationSettingTemplate = APIResult.M005A04['examination_template'][number];
  type ExaminationSettingTemplate = {
    /** テンプレートID */
    template_id: string;
    /** テンプレート名 */
    template_name: string;
    /** テンプレートメモ */
    template_memo: string;
    /** 追加アンケート有無 */
    enquete: string;
    /** アンケート作成期限 */
    limit_dtm: Date;
    /** URL1 */
    url1: string;
    /** URL2 */
    url2: string;
    /** サンプルURL */
    sample_url: string;
    /** 表示言語 */
    lang: string;
    /** デフォルト表示言語 */
    def_lang: string;
    /** 内部調査項目１ */
    add1: string;
    /** 内部調査項目２ */
    add2: string;
    /** 内部調査項目３ */
    add3: string;
    /** 審査区分 */
    examination_kbn: string;
    /** ステータス */
    status: string;
    /** リビジョン */
    revision: number;
  };
  /** 応募フォーム情報_テンプレート */
  // type ApplyFormTemplate = APIResult.M002A04['apply_form_template'][number];
  type ApplyFormTemplate = {
    /** テンプレートID */
    template_id: string;
    /** 審査No */
    examination_no: number;
    /** 応募フォームNo */
    apply_form_no: number;
    /** 言語区分 */
    lang: string;
    /** ソートNo */
    sort_no: number;
    /** 必須区分 */
    required: string;
    /** 質問内容 */
    question: string;
    /** 質問フォーマット */
    format: string;
    /** 質問区分 */
    q_kbn: string;
    /** 選択肢 */
    choices: string;
    /** 選択値 */
    value: string;
    /** スタイルシート */
    style: string;
    /** プレースホルダー */
    place_holder: string;
    /** 汎用項目１ */
    general1: string;
    /** 汎用項目２ */
    general2: string;
    /** 汎用項目３ */
    general3: string;
    /** リビジョン */
    revision: number;
  };
  /** オーディション基本情報_下書 */
  // type AuditionSettingDraft = APIResult.M002A09['audition_draft'][number];
  type AuditionSettingDraft = {
    /** オーディションID */
    audition_id: string;
    /** 言語 */
    lang: string;
    /** 下書区分 */
    draft_kbn: string;
    /** タイトル */
    title: string;
    /** 所属 */
    sgroup: number;
    /** URL1 */
    url1: string;
    /** URL2 */
    url2: string;
    /** サンプルURL */
    sample_url: string;
    /** 公開開始日時 */
    start_dtm: Date;
    /** 公開終了日時 */
    end_dtm: Date;
    /** ドメイン指定解除テキスト */
    domain_txt: string;
    /** 完了画面カスタムテキスト */
    end_custom_txt: string;
    /** 応募完了メール・件名 */
    mail_title: string;
    /** 応募完了メール・送信元 */
    mail_from: string;
    /** 応募完了メール・本文 */
    mail_txt: string;
    /** 受付前テキスト */
    start_txt: string;
    /** 終了後テキスト */
    end_txt: string;
    /** 有効フラグ */
    valid_flg: string;
    /** 表示言語 */
    display_lang: string;
    /** デフォルト表示言語 */
    def_lang: string;
    /** 作成者 */
    author: string;
    /** ステータス */
    status: string;
    /** LPURL */
    lp_url: string;
    /** GoogleTag */
    googletag: string;
    /** GoogleTag2 */
    googletag2: string;
    /** 募集ページ画像 */
    image: string;
    /** 募集ページ詳細 */
    detail: string;
    /** グループ応募可否 */
    group_ap: number;
    /** 特定利用 */
    specific_use: string;
    /** プライバシーポリシー */
    privacy_policy: string;
    /** オーディション応募規約 */
    terms: string;
    /** リビジョン */
    revision: number;
  };
  /** 審査情報_下書 */
  // type ExaminationSettingDraft = APIResult.M005A09['examination_draft'][number];
  type ExaminationSettingDraft = {
    /** オーディションID */
    audition_id: string;
    /** 審査no */
    examination_no: number;
    /** 下書区分 */
    draft_kbn: string;
    /** 追加アンケート有無 */
    enquete: string;
    /** アンケート作成期限 */
    limit_dtm: Date;
    /** URL1 */
    url1: string;
    /** URL2 */
    url2: string;
    /** サンプルURL */
    sample_url: string;
    /** 表示言語 */
    lang: string;
    /** デフォルト表示言語 */
    def_lang: string;
    /** 内部調査項目１ */
    add1: string;
    /** 内部調査項目２ */
    add2: string;
    /** 内部調査項目３ */
    add3: string;
    /** 審査区分 */
    examination_kbn: string;
    /** ステータス */
    status: string;
    /** リビジョン */
    revision: number;
  };
  /** 応募フォーム情報_下書 */
  // type ApplyFormDraft = APIResult.M002A09['apply_form_draft'][number] | APIResult.M005A09['apply_form_draft'][number];
  type ApplyFormDraft =
    | {
        /** オーディションID */
        audition_id: string;
        /** 審査No */
        examination_no: number;
        /** 応募フォームNo */
        apply_form_no: number;
        /** 言語区分 */
        lang: string;
        /** ソートNo */
        sort_no: number;
        /** 下書区分 */
        draft_kbn: string;
        /** 必須区分 */
        required: string;
        /** 質問内容 */
        question: string;
        /** 質問フォーマット */
        format: string;
        /** 質問区分 */
        q_kbn: string;
        /** 選択肢 */
        choices: string;
        /** 選択値 */
        value: string;
        /** スタイルシート */
        style: string;
        /** プレースホルダー */
        place_holder: string;
        /** 汎用項目１ */
        general1: string;
        /** 汎用項目２ */
        general2: string;
        /** 汎用項目３ */
        general3: string;
        /** リビジョン */
        revision: number;
      }
    | {
        /** オーディションID */
        audition_id: string;
        /** 審査No */
        examination_no: number;
        /** 応募フォームNo */
        apply_form_no: number;
        /** 言語区分 */
        lang: string;
        /** ソートNo */
        sort_no: number;
        /** 下書区分 */
        draft_kbn: string;
        /** 必須区分 */
        required: string;
        /** 質問内容 */
        question: string;
        /** 質問フォーマット */
        format: string;
        /** 質問区分 */
        q_kbn: string;
        /** 選択肢 */
        choices: string;
        /** 選択値 */
        value: string;
        /** スタイルシート */
        style: string;
        /** プレースホルダー */
        place_holder: string;
        /** 汎用項目１ */
        general1: string;
        /** 汎用項目２ */
        general2: string;
        /** 汎用項目３ */
        general3: string;
        /** リビジョン */
        revision: number;
      };
  /** 初期表示時(status:"1" or "8" 以外)/各種テンプレート/下書選択時:引数 */
  export type SetCommonSettingsAndFormItemsArgs = {
    settingList: Omit<
      | AuditionSettingTemplate
      | AuditionSettingDraft
      | ExaminationSettingTemplate
      | ExaminationSettingDraft,
      'draft_kbn'
    >[];
    applyFormList: (ApplyFormTemplate | Omit<ApplyFormDraft, 'draft_kbn'>)[];
  };

  type TranslationKeys =
    | 'CH_001'
    | 'CH_002'
    | 'CH_003'
    | 'CH_004'
    | 'CH_005'
    | 'CH_006'
    | 'CH_007'
    | 'CH_008'
    | 'CH_009'
    | 'CH_010'
    | 'CH_011'
    | 'CH_012'
    | 'CH_013'
    | 'CH_014'
    | 'CH_015'
    | 'CH_016'
    | 'CH_017'
    | 'CH_018'
    | 'CH_019'
    | 'CH_020'
    | 'CH_021'
    | 'CH_022'
    | 'CH_023';
  /** コンテキスト型 */
  export type ContentsHandlerContextValue = {
    t: (...args: [key: TranslationKeys]) => string;
    onChangeArtistSameAsAccount?: (
      event: React.ChangeEvent<HTMLInputElement>,
      apply_form_no: { name: number; kana: number }
    ) => void;
    onClickTemplateAddressSearch?: (
      event: React.MouseEvent<HTMLButtonElement>,
      apply_form_no: {
        postcode?: number;
        prefecture?: number;
        city?: number;
        others?: number;
      }
    ) => void;
    /* Item内に状態を持てないためContextで親に状態を持たせる */
    applyType: 'GROUP' | 'SOLO';
    templateMembersIds: string[];
    setTemplateMembersIds: React.Dispatch<React.SetStateAction<string[]>>;
    onChangeApplyType: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    onChangeTemplateMembersNum: (memberNum: number) => void;
    onClickTemplateMembersDelete: (id: string) => void;
    register?: UseFormRegister<FieldValues>;
    isMoreSelected?: string[];
    onChangeCheckParts: (
      event: React.ChangeEvent<HTMLInputElement>,
      selectable: number,
      name: string,
      required?: boolean
    ) => void;
    onChangeSelectDateParts: (
      event: React.ChangeEvent<HTMLSelectElement>,
      apply_form_no: string,
      required?: boolean
    ) => void;
    hasErrorInSelectDate: boolean;
    hasErrorInMemberConsent: boolean;
    hasErrorConsentFiles: boolean;
    onChangePreferredVenueSelect: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    errors?: FieldErrors<FieldValues>;
  };
}

/** ファイルを扱うクラス */
export class FileHandler {
  private static fileMap: Map<string, File> = new Map();

  public static add(file: File) {
    const objectUrl = URL.createObjectURL(file);
    FileHandler.fileMap.set(objectUrl, file);
    return objectUrl;
  }

  public static get(objectUrl: string) {
    return FileHandler.fileMap.get(objectUrl);
  }

  public static getFileName(objectUrl: string) {
    return FileHandler.fileMap.get(objectUrl)?.name;
  }

  public static isMovie(objectUrl: string) {
    return !!FileHandler.fileMap.get(objectUrl)?.type.startsWith('video/');
  }

  public static remove(objectUrl: string) {
    const exists = FileHandler.fileMap.delete(objectUrl);
    if (exists) {
      URL.revokeObjectURL(objectUrl);
    }
  }

  public static clearAll() {
    const keys = Array.from(FileHandler.fileMap.keys());
    for (const objectUrl of keys) {
      FileHandler.fileMap.delete(objectUrl);
      URL.revokeObjectURL(objectUrl);
    }
  }

  /**
   * `img.src` に設定できるURLを `HTMLImageElement` に変換
   * @param src
   * @returns
   */
  private static convertImageSrc2HTMLImage(src: string) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }

  /**
   * `HTMLImageElement` から `HTMLCanvasElement` に変換
   * @param img
   * @param size リサイズする場合、幅/高さを指定
   * @returns
   */
  private static convertHTMLImage2DataUrl(
    img: HTMLImageElement,
    size: { width?: number; height?: number } = {}
  ) {
    const { naturalWidth, naturalHeight } = img;
    const { width = naturalWidth, height = naturalHeight } = size;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to getContext from canvas');
    }
    ctx.drawImage(img, 0, 0, width, height);
    return canvas;
  }

  /**
   * `HTMLCanvasElement` から `DataUrl` を生成
   * @param canvas
   * @param mimeType
   * @returns
   */
  private static convertCanvas2DataUrl(canvas: HTMLCanvasElement, mimeType: string = 'image/png') {
    return canvas.toDataURL(mimeType);
  }

  /**
   * `DataUrl` を `File` に変換
   * @param dataUrl
   * @param fileName
   * @param mimeType
   * @returns
   */
  private static async convertDataUrl2File(dataUrl: string, fileName: string, mimeType?: string) {
    const blob = await (await fetch(dataUrl)).blob();
    return new File([blob], fileName, { type: mimeType });
  }
}

export namespace Layout {
  // ------------------------------
  // レイアウト関連
  // ------------------------------
  /** 背景のタイプ(色/ファイル) */
  export type LayoutBackgroundType = 'color' | 'media';
  /** レイアウト設定情報 */
  export type LayoutConfig = {
    type: LayoutType;
    include: boolean;
    bgType?: LayoutBackgroundType;
    font?: string;
    height?: string;
    colors?: string[];
    fileObjectUrls?: string[];
    copywrites?: string;
    textAlign?: Types.TextAlign;
    textDecorations?: Types.TextDecoration[];
    fontSize?: string;
    fontColor?: string;
    backTo?: string;
  };
  export type LayoutType = /* 廃止'header' */ 'body' | 'footer';
  export type LayoutConfigs = {
    [K in LayoutType]?: LayoutConfig;
  };

  /**
   * LayoutConfigからstylesを生成
   */
  export function transformLayoutConfigStyles(
    layoutConfig: LayoutConfig
  ): Types.ApplyForm['styles'] {
    const { textAlign, textDecorations = [], fontSize, fontColor } = layoutConfig;
    const fontWeight = textDecorations.includes(Consts.PROP_NAMES.TEXT.DECORATION.BOLD)
      ? 'bold'
      : undefined;
    const fontStyle = textDecorations.includes(Consts.PROP_NAMES.TEXT.DECORATION.ITALIC)
      ? 'italic'
      : undefined;
    const textDecoration = textDecorations.includes(Consts.PROP_NAMES.TEXT.DECORATION.UNDERLINE)
      ? 'underline'
      : undefined;

    return {
      height: layoutConfig.height,
      // 現状、単色での背景色の設定ができれば十分
      'background-color': layoutConfig.colors?.[0],
      'text-align': textAlign,
      'font-weight': fontWeight,
      'font-style': fontStyle,
      'text-decoration': textDecoration,
      'font-size': fontSize,
      color: fontColor,
    };
  }
  /**
   * stylesからLayoutConfigを生成
   */
  export function transformStylesLayoutConfig(
    styles: Types.ApplyForm['styles']
  ): Pick<
    LayoutConfig,
    | 'colors'
    | 'bgType'
    | 'fontColor'
    | 'fontSize'
    | 'height'
    | 'textAlign'
    | 'textDecorations'
    | 'font'
  > {
    const {
      height,
      'background-color': backgroundColor,
      'text-align': textAlign,
      'font-weight': fontWeight,
      'font-style': fontStyle,
      'text-decoration': textDecoration,
      'font-size': fontSize,
      color: fontColor,
    } = styles ?? {};
    return {
      height,
      colors: backgroundColor != null ? [backgroundColor] : undefined,
      textAlign: textAlign as Types.TextAlign,
      textDecorations: [
        fontWeight === 'bold' ? Consts.PROP_NAMES.TEXT.DECORATION.BOLD : undefined,
        fontStyle === 'italic' ? Consts.PROP_NAMES.TEXT.DECORATION.ITALIC : undefined,
        textDecoration === 'underline' ? Consts.PROP_NAMES.TEXT.DECORATION.UNDERLINE : undefined,
      ].filter(Boolean) as Types.TextDecoration[],
      fontSize,
      fontColor,
      font: undefined, // TODO
    };
  }

  /**
   * LayoutConfigsをApplyFormに変換
   */
  export function transformLayoutConfigsApplyFormList(
    layoutConfigs: LayoutConfigs
  ): Omit<Types.ApplyForm, 'examination_no' | 'template_id'>[] {
    const { body, footer } = layoutConfigs;
    const bodyApplyForm: Omit<Types.ApplyForm, 'examination_no' | 'template_id'> | undefined =
      body && {
        apply_form_no: Consts.RESERVED_APPLY_FORM_IDS.body,
        format: '92',
        sort_no: Consts.RESERVED_APPLY_FORM_IDS.body,
        styles: body ? transformLayoutConfigStyles(body) : {},
      };
    const footerApplyForm: Omit<Types.ApplyForm, 'examination_no' | 'template_id'> | undefined =
      footer?.include === true
        ? {
            apply_form_no: Consts.RESERVED_APPLY_FORM_IDS.footer,
            format: '91',
            sort_no: Consts.RESERVED_APPLY_FORM_IDS.footer,
            styles: footer ? transformLayoutConfigStyles(footer) : {},
            general1: footer?.copywrites,
            general2: footer?.backTo,
          }
        : {
            apply_form_no: Consts.RESERVED_APPLY_FORM_IDS.footer,
            format: '91',
            sort_no: Consts.RESERVED_APPLY_FORM_IDS.footer,
            styles: {},
          };
    return [bodyApplyForm, footerApplyForm].filter(
      (form): form is Omit<Types.ApplyForm, 'examination_no' | 'template_id'> => form !== undefined
    );
  }

  /**
   * APIで取得した結果をApplyFormとLayoutConfigsに変換
   */
  export function transformApplyFormAndLayoutConfigs<
    T extends {
      apply_form_no: number;
      sort_no: number;
      style: string | null | undefined;
      general1: string | undefined;
      general2: string | undefined;
    }[],
  >(
    applyForms: T
  ): {
    applyForms: T;
    layoutConfigs: LayoutConfigs;
  } {
    const body = applyForms.find(
      ({ apply_form_no }) => apply_form_no === Consts.RESERVED_APPLY_FORM_IDS.body
    );
    const footer = applyForms.find(
      ({ apply_form_no }) => apply_form_no === Consts.RESERVED_APPLY_FORM_IDS.footer
    );
    const filteredApplyForms = applyForms.filter(
      ({ apply_form_no }) =>
        !Array.from<number>([
          Consts.RESERVED_APPLY_FORM_IDS.body,
          Consts.RESERVED_APPLY_FORM_IDS.footer,
        ]).includes(apply_form_no)
    );

    const bodyLayoutConfig: LayoutConfig | undefined = body && {
      type: 'body',
      include: true,
      ...transformStylesLayoutConfig(Utils.parseStyle(body.style)),
    };
    const footerLayoutConfig: LayoutConfig | undefined = footer && {
      type: 'footer',
      include: true,
      ...transformStylesLayoutConfig(Utils.parseStyle(footer.style)),
      copywrites: footer.general1,
      backTo: footer.general2,
    };
    return {
      applyForms: filteredApplyForms as T,
      layoutConfigs: {
        body: bodyLayoutConfig,
        footer: footerLayoutConfig,
      },
    };
  }

  type DynamicKeyframeProps = {
    name: string;
    values: { progress: number; css: string }[];
  };

  export function DynamicKeyframe({ name, values }: DynamicKeyframeProps) {
    return (
      <style>
        @keyframes {name} {'{'}
        {values.map(({ progress, css }) => {
          return `${progress}% ${css}\n`;
        })}
        {'}'}
      </style>
    );
  }

  export function buildLayout(config: LayoutConfig | undefined, keyframesName: string = '') {
    let contents: ReactNode = null;
    let style: CSSProperties | undefined = undefined;
    let keyframes: ReactNode = null;
    if (!config) {
      contents = '';
      style = undefined;
    } else {
      const {
        type,
        include,
        font,
        colors = [],
        fileObjectUrls = [],
        bgType = 'color',
        height,
        textAlign,
        textDecorations = [],
        fontSize,
        fontColor,
        copywrites,
        backTo,
      } = config;
      const isFooter = type === 'footer';
      if (!include) {
        contents = null;
        style = { paddingTop: 0, paddingBottom: 0 };
      } else {
        const fontWeight = textDecorations.includes(Consts.PROP_NAMES.TEXT.DECORATION.BOLD)
          ? 'bold'
          : undefined;
        const fontStyle = textDecorations.includes(Consts.PROP_NAMES.TEXT.DECORATION.ITALIC)
          ? 'italic'
          : undefined;
        const textDecoration = textDecorations.includes(Consts.PROP_NAMES.TEXT.DECORATION.UNDERLINE)
          ? 'underline'
          : undefined;
        const textStyle: CSSProperties = {
          textAlign,
          fontWeight,
          fontStyle,
          textDecoration,
          fontSize,
          color: fontColor,
        };
        const backToElement = isFooter && backTo != null && backTo !== '' && (
          <ContentsHandlerContext.Consumer>
            {({ t }) => (
              <a
                href={backTo}
                rel="noopener noreferrer"
                aria-label={t('CH_011')}
                className="border-none basis-auto grow-0"
              >
                {t('CH_011')}
              </a>
            )}
          </ContentsHandlerContext.Consumer>
        );

        contents = (
          <div className="flex flex-row items-center">
            {backToElement}
            {isFooter && copywrites != null && (
              <span className="grow" style={textStyle}>
                {copywrites}
              </span>
            )}
          </div>
        );
        style = {
          height: height || 'auto',
          fontFamily: font !== undefined ? `'${font}'` : undefined,
        };
        if (colors.length === 1) {
          style.backgroundColor = colors[0];
        } else if (colors.length > 1) {
          // keyframes生成
          const keyframesConfig = colors.map((color, index) => {
            return {
              progress: (100 / colors.length) * index,
              css: `{ background-color: ${color}; }\n`,
            };
          });
          keyframesConfig.push({ progress: 100, css: `{ background-color: ${colors[0]}; }\n` });

          style.animation = `${keyframesName} 5s ease infinite`;
          keyframes = <DynamicKeyframe name={keyframesName} values={keyframesConfig} />;
          console.log({ style, keyframesName });
        }

        if (bgType !== 'color' && fileObjectUrls.length > 0) {
          const movieFile = fileObjectUrls.find((url) => FileHandler.isMovie(url));
          if (movieFile) {
            // 動画ファイルがあった場合
            contents = (
              <div className="absolute top-0 left-0 z-[-1]">
                <video src={movieFile} loop muted autoPlay />
              </div>
            );
            style.backgroundColor = 'transparent';
          } else {
            // 動画ファイルがなかった場合
            style.backgroundImage = `${fileObjectUrls
              .reverse()
              .map((url) => `url(${url})`)
              .join(', ')}`;
          }
        }
      }
    }

    return { contents, style, keyframes };
  }
}

export const TAB = {
  COMMENT: 0,
  SELF_PR: 1, // 新規追加: 自己PR
  PERSONAL_INFO: 2,
  EXAMINATION_INFO: 3,
  MESSAGE: 4, // 番号変更: 1 → 4
} as const;
